import React, {
  useCallback, useEffect, useState, useRef, useMemo, RefObject, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import Card from '../../components/card/card.component';
import Table from '../../components/table/table.component';
import TableCell from '../../components/table-cell/table-cell.component';
import Tooltip from '../../components/tooltip/tooltip.component';
import DraggableItem from '../../components/draggable-item/draggable-item.component';
import ContentHeader from '../../components/content-header/content-header.component';
import HiddenBlock from '../../components/hidden-block/hidden-block.component';
import ExpansionGroup from '../../components/expansion-group/expansion-group.component';
import LocationMap from '../../components/location-map/location-map.component';
import Loader from '../../components/loader/loader.component';
import CheckAccess from '../../components/check-access/check-access.component';
import LongClickListItem from '../../components/long-click-list-item/long-click-list-item.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import FilteredCell from '../../components/filtered-cell/filtered-cell.component';
import TableResize from '../../components/table-resize/table-resize.component';
import RequiredMark from '../../components/required-mark/required-mark.component';
import ContextMenu from '../../components/context-menu/context-menu.component';
import FilteredLink from '../../components/filtered-link/filtered-link.component';
import ChipLink from '../../components/chip-link/chip-link.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import TableSortLabel from '../../components/table-sort-label/table-sort-label.component';
import NoContentBlock from '../../components/no-content-block/no-content-block.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import ActionButton from '../../components/action-button/action-button.component';
import Image from '../../components/image/image.component';
import Checkbox from '../../components/checkbox/checkbox.component';
import TableHead from '../../components/table-head/table-head.component';
import TableBody from '../../components/table-body/table-body.component';
import List from '../../components/list/list.component';
import ListItemImage from '../../components/list-item-image/list-item-image.component';
import ListItemText from '../../components/list-item-text/list-item-text.component';
import ListItemCheckbox from '../../components/list-item-checkbox/list-item-checkbox.component';
import PaddedFlexContainer from '../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../components/flex-container/flex-container.component';
import Form from '../../components/form/form.component';
import FormContainer from '../../components/form-container/form-container.component';
import FormError from '../../components/form-error/form-error.component';
import ActionButtonContainer from '../../components/action-button-container/action-button-container.component';
import FilteredText from '../../components/filtered-text/filtered-text.component';
import FileLink from '../../components/file-link/file-link.component';
import ExpansionGroupWidget from '../../components/expansion-group-widget/expansion-group-widget.component';
import TableRowGroupHeader from '../../components/table-row-group-header/table-row-group-header.component';
import TableRowGroupName from '../../components/table-row-group-name/table-row-group-name.component';
import LastUpdateBlock from '../app/common/last-update-block/last-update-block.component';
import ControlButtonBlock from '../app/common/control-button-block/control-button-block.component';
import ContentListContainerOffset
  from '../app/common/content-list-container-offset/content-list-container-offset.component';
import ExportModal from '../app/controls/export-modal/export-modal.component';
import StatusModal from '../app/controls/status-modal/status-modal.component';
import StatusFilter from '../app/controls/status-filter/status-filter.component';
import ShareModal from '../app/controls/share-modal/share-modal.component';
import FileUpdateModal from '../app/controls/file-update-modal/file-update-modal.component';
import Controls from '../app/controls/controls.component';
import PortfolioFilter from '../app/controls/portfolio-filter/portfolio-filter.component';
import ActivityModal from '../activity-list/activity-modal/activity-modal.component';
import DealFileModal from '../deal/deal-file-modal/deal-file-modal.component';
import DealStatusChangesModal from '../deal/deal-status-changes-modal/deal-status-changes-modal.component';
import DealNewModal from './deal-new-modal/deal-new-modal.component';
import DealRow from './deal-row/deal-row.component';
import EditIcon from '../../icons/edit-icon';
import ExpandIcon from '../../icons/expand-icon';
import CollapseIcon from '../../icons/collapse-icon';
import MoreIcon from '../../icons/more-icon';
import ChangeStatusIcon from '../../icons/change-status-icon';
import DeleteItemIcon from '../../icons/delete-item-icon';
import AttachmentIcon from '../../icons/attachment-icon';
import AddCommentIcon from '../../icons/add-comment-icon';
import SendEmailIcon from '../../icons/send-email-icon';
import FileUpdateIcon from '../../icons/file-update-icon';
import ArrowLinkIcon from '../../icons/arrow-link-icon';
import DealListValidator from './deal-list.validator';
import SortService from '../../services/sort-service';
import StructureService from '../../services/structure-service';
import FormatService from '../../services/format-service';
import ScreenService from '../../services/screen-service';
import FormService from '../../services/form-service';
import DateService from '../../services/date-service';
import useScroll from '../../hooks/use-scroll';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useArrowNavigation from '../../hooks/use-arrow-navigation';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useScreenMobile from '../../hooks/use-screen-mobile';
import useScreenSmall from '../../hooks/use-screen-small';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import {
  toggleExportMode, toggleEditMode, toggleCustomizeMode, toggleSelectMode, saveTableColumnSettings,
} from '../app/controls/controls.action';
import { deleteActivityComment, saveActivityComment } from '../activity/activity.action';
import {
  getDealListData, getDealStatusChangeListData, deleteDealList, shareDealData, saveDealListData, saveDealStatusList,
  saveDealListFile, updateFileDealList, resetDealStatusChangeListData,
} from './deal-list.action';
import {
  setPortfolioIdsFilter, setPortfolioGroupIdsFilter,
} from '../app/controls/portfolio-filter/portfolio-filter.action';
import {
  getListActivityDealData, getListContactCompanyData, getListContactData, getListContactPersonData,
  getListContactPersonInternalData, getListDealAssetData, getListTagData,
} from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import setStatusFilter from '../app/controls/status-filter/status-filter.action';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../constants/system/sort-types';
import {
  DEAL_CREATE, DEAL_LIST_DEAL_LOCATIONS_VIEW, ACTIVITY_COMMENT_CREATE, DEAL_COMMON_EDIT,
  DEAL_LIST_PORTFOLIO_FILTER_VIEW, DEAL_LIST_VIEW, DEAL_LIST_IMPORT, DEAL_LIST_EXPORT, DEAL_DELETE,
  DEAL_FILE_CREATE, DEAL_STATUS_EDIT, CONTACT_VIEW, DEAL_VIEW, SEARCH_RESULT_VIEW, ACTIVITY_COMMENT_LIST_VIEW,
  ACTIVITY_ATTACHMENT_LIST_VIEW,
} from '../../constants/entity/permissions';
import { FORMAT_LIST_EXPORT_DEAL, FORMAT_LIST_IMPORT_DEAL } from '../../constants/system/format-lists';
import { FORM_ID_DEAL_LIST } from '../../constants/system/form-ids';
import {
  ROUTE_CONTACT, ROUTE_DEAL_LIST, ROUTE_SEARCH, ROUTE_DEAL, ROUTE_PARAM_ID_NEW,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_TAG_ID, QUERY_PARAM_EDIT_MODE, QUERY_PARAM_TEMPLATE, QUERY_PARAM_STATUS, QUERY_PARAM_PORTFOLIO_IDS,
  QUERY_PARAM_PORTFOLIO_GROUP_IDS, QUERY_PARAM_CREATE,
} from '../../constants/system/query-params';
import { DATA_DEAL_ID, DATA_DEAL_STATUS } from '../../constants/system/data-attributes';
import { PAGE_LIST_DEFAULT } from '../../constants/system/pages';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../constants/entity/contact-types';
import { DEAL_STATUS_DEAD_DEAL, DEAL_STATUS_DRAFT } from '../../constants/entity/deal-statuses';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../constants/entity/setting-names';
import { IMPORT_EXPORT_ENTITY_TYPE_DEAL_LIST } from '../../constants/system/import-export-entity-types';
import { TABLE_NAME_DEAL_LIST } from '../../constants/system/table-names';
import { MARKER_COLOR_RED } from '../../constants/entity/marker-colors';
import { SIGN_DOT } from '../../constants/system/signs';
import LINK_TYPE_FILE_LINK from '../../constants/entity/link-types';
import COMMENT_TYPE_SYSTEM from '../../constants/entity/comment-types';
import AssetDefaultImage from '../../static/images/asset-default-image.png';
import CompanyDefaultImage from '../../static/images/company-default-image.png';
import PersonDefaultImage from '../../static/images/person-default-image.png';

const DealList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ID_PREFIX_CHECKBOX_EXPORT_GROUP: string = 'checkbox-export-group';

  const {
    isEditMode,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isDataLoaded,
    isRightDrawerOpen,
    isLoading,
    isActivityCommentSaving,
    currentUser,
    newCommentList,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    statusFilterStatus,
    dealListData,
    dealsTotal,
    portfolioGroupList,
    dealTemplateList,
    dealStatusList,
    assetTypeList,
    contactPersonInternalList,
    contactCompanyList,
    contactPersonList,
    dealTypeList,
    dealRejectReasonList,
    measureUnitList,
    assetOwnershipStructureTypeList,
    dealLoanTypeList,
    accessLevelList,
    contactLinkedUserList,
    tagList,
    isDealStatusChangeListLoading,
    dealStatusChangeListData,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    initialValues: initialFormValues,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const isMobileScreen: boolean = useScreenMobile();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const permissionList: string[] = useCurrentUserPermissions();
  const tableRefs: RefObject<Record<string, HTMLTableElement>> = useRef({});
  const headerCellRefs: RefObject<Record<string, Record<string, HTMLElement>>> = useRef({});
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);
  const dispatch: ReduxDispatch = useDispatch();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryCreate: boolean | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CREATE]
    ? FormatService.extractBooleanValue(locationQueryParams[QUERY_PARAM_CREATE].toString()) : null;
  const queryFilterStatus: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_STATUS]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_STATUS].toString()) : null;
  const queryFilterPortfolioIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS].toString().split(SIGN_DOT)
        .map((portfolioId: string): number => FormatService.parseInt(portfolioId)) : null
  );
  const queryFilterPortfolioGroupIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS].toString().split(SIGN_DOT)
        .map((portfolioGroupId: string): number => FormatService.parseInt(portfolioGroupId)) : null
  );

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const REASON_STATUS_LIST: string[] = useMemo((): string[] => [
    DEAL_STATUS_DEAD_DEAL,
  ], []);

  const [page, setPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [sortParams, setSortParams]: [Record<string, SortParams> | null, Function] = useState<StateValueDefault>(null);
  const [isExportModalOpen, setIsExportModalOpen]: [boolean, Function] = useState(false);
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [selectedRows, setSelectedRows]: [Record<string, number[]>, Function] = useState({});
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [dealActiveId, setDealActiveId]: [number | null, Function] = useState(null);
  const [dealActiveIds, setDealActiveIds]: [number[], Function] = useState([]);
  const [selectedRowsDefault, setSelectedRowsDefault]: [Record<string, number[]>, Function] = useState({});
  const [isDealNewModalOpen, setIsDealNewModalOpen]: [boolean, Function] = useState(false);
  const [expandedGroups, setExpandedGroups]: [number[], Function] = useState([]);
  const [extendedTableFields, setExtendedTableFields]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [tableColumnSettings, setTableColumnSettings]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [statusFilterText, setStatusFilterText]: [string, Function] = useState('');
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastStatusValue, setLastStatusValue]: [string | null, Function] = useState(null);
  const [lastPortfolioIdsValue, setLastPortfolioIdsValue]: [number[] | null, Function] = useState(null);
  const [lastPortfolioGroupIdsValue, setLastPortfolioGroupIdsValue]: [number[] | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isDealStatusChangesModalOpen, setIsDealStatusChangesModalOpen]: [boolean, Function] = useState(false);
  const [isNewDealActivityModalOpen, setIsNewDealActivityModalOpen]: [boolean, Function] = useState(false);
  const [isDealFileModalOpen, setIsDealFileModalOpen]: [boolean, Function] = useState(false);
  const [isDealListFileUpdateModalOpen, setIsDealListFileUpdateModalOpen]: [boolean, Function] = useState(false);
  const [isCommentFormActive, setIsCommentFormActive]: [boolean, Function] = useState(false);
  const [newDealActivityItemData, setNewDealActivityItemData]: [Activity | null, Function]
    = useState<StateValueDefault>(null);
  const [dealFileFile, setDealFileFile]: [File | null, Function] = useState(null);
  const [dealFileGroupList, setDealFileGroupList]: [DealFileGroup | null, Function] = useState(null);
  const [withAllRowsSelected, setWithAllRowsSelected]: [boolean, Function] = useState(false);
  const [isStatusChangeModalOpen, setIsStatusChangeModalOpen]: [boolean, Function] = useState(false);
  const [statusModalErrorMessage, setStatusModalErrorMessage]: [string | null, Function] = useState(null);
  const [dealStatusChangesModalErrorMessage, setDealStatusChangesModalErrorMessage]: [string | null, Function]
    = useState(null);
  const [dealStatusChangesModalDealName, setDealStatusChangesModalDealName]: [string | null, Function] = useState(null);
  const [inlineSaveDealId, setInlineSaveDealId]: [number | null, Function] = useState(null);
  const [inlineSaveIsConfirm, setInlineSaveIsConfirm]: [boolean | null, Function] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen]: [boolean, Function] = useState(false);
  const [shareModalErrorMessage, setShareModalErrorMessage]: [string | null, Function] = useState(null);

  const pageTitle: string = `Deal Pipeline${statusFilterText ? `: ${statusFilterText}` : ''}`;
  const createButtonTitle: string | null = !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
    ? 'New Deal' : null;
  const isGroupExpanded: boolean = expandedGroups && !!expandedGroups.length;
  const isCustomizable: boolean = !isSmallScreen;
  const deleteConfirmModalTitle: string = dealActiveIds && dealActiveIds.length > 1 ? 'Delete Deals' : 'Delete Deal';
  const deleteConfirmModalText: string = dealActiveIds && dealActiveIds.length > 1
    ? 'Are you sure you want to delete these deals?'
    : 'Are you sure you want to delete this deal?';
  const shareModalTitle: string = 'Share Deal Info';
  const shareModalDescription: string = 'This action will send deal info to selected user\'s email';
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);
  const dealListViewPermissions: string[] = useMemo((): string[] => [DEAL_LIST_VIEW], []);
  const dealListPortfolioFilterViewPermissions: string[] = useMemo((): string[] => [
    DEAL_LIST_PORTFOLIO_FILTER_VIEW,
  ], []);
  const activityCommentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_LIST_VIEW], []);
  const activityAttachmentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_ATTACHMENT_LIST_VIEW], []);

  const dealAssetList: Asset[] = useMemo((): Asset[] => (
    dealListData && dealListData.length
      ? dealListData.flatMap((dealStatus: DealStatus): Deal[] => dealStatus.deals).map((dealItem: Deal): Asset => (
        (dealItem.asset && {
          ...dealItem.asset,
          lastDeal: dealItem,
        }) || null
      )).filter((assetItem: Asset): boolean => !!assetItem) : []
  ), [dealListData]);
  const portfolioList: Portfolio[] = useMemo((): Portfolio[] => (
    portfolioGroupList && portfolioGroupList.length ? portfolioGroupList.reduce((
      resultPortfolioList: Portfolio[], portfolioGroup: PortfolioGroup,
    ): Portfolio[] => ([
      ...resultPortfolioList,
      ...portfolioGroup.portfolios,
    ]), []) : []
  ), [portfolioGroupList]);
  const commonDealsTotal: number = useMemo((): number => (
    dealListData && dealListData.length ? dealListData.reduce((
      dealItemsTotal: number, dealGroupItem: DealStatus,
    ): number => dealItemsTotal + dealGroupItem.deals.length, 0) : 0
  ), [dealListData]);
  const dealsSelectedTotal: number = useMemo((): number => (
    selectedRows ? Object.values(selectedRows)
      .reduce((resultTotal: number, selectedRowIds: number[]): number => resultTotal + selectedRowIds.length, 0) : 0
  ), [selectedRows]);
  const commonDealStatusList: DealStatus[] = useMemo((): DealStatus[] => (
    dealStatusList && dealStatusList.length ? dealStatusList : []
  ), [dealStatusList]);
  const filteredCommonDealStatusList: DealStatus[] = useMemo((): DealStatus[] => (
    commonDealStatusList.filter((dealStatusItem: DealStatus): boolean => dealStatusItem.name !== DEAL_STATUS_DRAFT)
  ), [commonDealStatusList]);
  const filteredDealStatusList: DealStatus[] = useMemo((): DealStatus[] => (
    (commonDealStatusList && commonDealStatusList.length && (
      statusFilterStatus && statusFilterStatus === DEAL_STATUS_DEAD_DEAL
        ? commonDealStatusList : commonDealStatusList.filter((dealStatusItem: DealStatus): boolean => (
          dealStatusItem.name !== DEAL_STATUS_DEAD_DEAL
        ))
    )) || []
  ), [commonDealStatusList, statusFilterStatus]);
  const filteredPortfolioGroupList: PortfolioGroup[] = useMemo((): PortfolioGroup[] => (
    portfolioGroupList && portfolioGroupList.filter((portfolioGroupItem: PortfolioGroup): boolean => (
      !!portfolioGroupItem.portfolios.length
    ))
  ), [portfolioGroupList]);
  const portfolioMarkerColorIdList: number[] = useMemo((): number[] => (
    portfolioList && portfolioList.length ? portfolioList.map((portfolioItem: Portfolio): number | null => (
      portfolioItem.markerColor ? portfolioItem.markerColor.id : null
    )).filter((portfolioMarkerColorId: number | null): boolean => !!portfolioMarkerColorId) as number[] : []
  ), [portfolioList]);
  const dealAssetLocationItemList: LocationMapItem[] = useMemo((): LocationMapItem[] => (
    dealAssetList && dealAssetList.length
      ? dealAssetList.map((assetItem: Asset): LocationMapItem => ({
        id: assetItem.id,
        title: assetItem.lastDeal ? assetItem.lastDeal.name : null,
        color: (assetItem.portfolio && assetItem.portfolio.markerColor && assetItem.portfolio.markerColor
          && assetItem.portfolio.markerColor.name) || null,
        link: assetItem.lastDeal ? `${ROUTE_DEAL}/${assetItem.lastDeal.id}` : null,
        amount: (assetItem.lastDeal && assetItem.lastDeal.amount) || null,
        imageLink: assetItem.mainPhotoThumbnail || assetItem.mainPhoto,
        latitude: assetItem.latitude || null,
        longitude: assetItem.longitude || null,
      })) : []
  ), [dealAssetList]);
  const scrollDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealsTotal, commonDealsTotal, isLoading, isEditMode, isCustomizeMode,
  ], [dealsTotal, commonDealsTotal, isLoading, isEditMode, isCustomizeMode]);
  const tableColumnDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isCustomizeMode, tableColumnSettings,
  ], [isCustomizeMode, tableColumnSettings]);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const isFormDataPristine: boolean = isFormPristine;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isExportMode || isSelectMode || isCustomizeMode
    || !!statusFilterStatus;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && !!lastLocation;
  const isStatusFilterVisible: boolean = dealStatusList && !!dealStatusList.length
    && StructureService.checkPermission(DEAL_LIST_VIEW, permissionList);
  const withMap: boolean = !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode;
  const withButtons: boolean = isEditMode || isExportMode || isSelectMode;
  const withStatusFilter: boolean = isStatusFilterVisible && !isSmallScreen;
  const isPortfolioFilterViewAllowed: boolean = StructureService.checkPermission(
    DEAL_LIST_PORTFOLIO_FILTER_VIEW, permissionList,
  );

  let commonRowIndex: number = 0;
  let isTableLoading: boolean = false;

  const onActivityDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListActivityDealData({
      query,
    }));
  }, []);

  const onActivityDealSearchClose: Function = useCallback((): void => {
    dispatch(getListActivityDealData({
      query: null,
    }));
  }, []);

  const onDealAssetSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealAssetData({
      query,
    }));
  }, []);

  const onDealAssetSearchClose: Function = useCallback((): void => {
    dispatch(getListDealAssetData({
      query: null,
    }));
  }, []);

  const onContactPersonInternalSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonInternalData({
      query,
    }));
  }, []);

  const onContactPersonInternalSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonInternalData({
      query: null,
    }));
  }, []);

  const onContactSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactData({
      query,
    }));
  }, []);

  const onContactSearchClose: Function = useCallback((): void => {
    dispatch(getListContactData({
      query: null,
    }));
  }, []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonData({
      query,
    }));
  }, []);

  const onContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonData({
      query: null,
    }));
  }, []);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onCommentEntityLinkClick: Function = useCallback((link: string): void => {
    navigate(link);
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    if (inlineSaveDealId !== null) {
      inlineCancel();
    } else {
      FormService.forceResetForm(resetForm, formProps);

      setWithAllRowsSelected(false);
      setDealActiveIds([]);
      setDealFileFile(null);

      if (dealListData) {
        dealListData.forEach((dealGroupItem: DealStatus): void => {
          selectedRowsDefault[dealGroupItem.id] = [];
        });
      }

      setSelectedRows(selectedRowsDefault);

      dispatch(toggleExportMode(false));
      dispatch(toggleSelectMode(false));
      dispatch(toggleEditMode(false));
      dispatch(toggleCustomizeMode(false));
    }
  }, [dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const updateQueryParams: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_DEAL_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, statusFilterStatus, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const openDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealId: number,
  ): void => {
    setDealActiveIds(dealId ? [dealId] : (selectedRows && Object.values(selectedRows)
      .reduce((resultDealItemIds: number[], dealItemIds: number[]): number[] => ([
        ...resultDealItemIds,
        ...dealItemIds,
      ]), [])));
    setIsDeleteConfirmModalOpen(true);
  }, [selectedRows]);

  const openDealStatusChangesModal: Function = useCallback((dealId: number, dealName: string | null): void => {
    setIsDealStatusChangesModalOpen(true);
    setDealStatusChangesModalErrorMessage(null);
    setDealStatusChangesModalDealName(dealName);

    if (dealId) {
      dispatch(getDealStatusChangeListData({
        dealId,
      }, null, (exception: ServerError): void => {
        if (exception && exception.message) {
          setDealStatusChangesModalErrorMessage(exception.message);
        }
      }));
    }
  }, []);

  const closeDealStatusChangesModal: Function = useCallback((): void => {
    setIsDealStatusChangesModalOpen(false);
    setDealStatusChangesModalErrorMessage(null);
    setDealStatusChangesModalDealName(null);

    dispatch(resetDealStatusChangeListData());
  }, []);

  const openNewDealActivityModal: Function = useCallback((
    newDealActivityItem: Activity, isCommentActive?: boolean,
  ): void => {
    setNewDealActivityItemData({ ...newDealActivityItem });

    if (isCommentActive) {
      setIsCommentFormActive(isCommentActive);
    }

    setIsNewDealActivityModalOpen(true);
  }, []);

  const closeNewDealActivityModal: Function = useCallback((): void => {
    setIsNewDealActivityModalOpen(false);
    setIsCommentFormActive(false);
    setNewDealActivityItemData(null);
  }, []);

  const openDealListFileUpdateModal: Function = useCallback((): void => {
    setIsDealListFileUpdateModalOpen(true);
  }, []);

  const closeDealListFileUpdateModal: Function = useCallback((): void => {
    setIsDealListFileUpdateModalOpen(false);
  }, []);

  const onNewDealActivitySubmitCreateForm: Function = useCallback((onSubmitCallback: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const openNewDealFileModal: Function = useCallback((dealId: number): void => {
    setIsDealFileModalOpen(true);
    setDealActiveId(dealId);
  }, []);

  const closeDealFileModal: Function = useCallback((): void => {
    setIsDealFileModalOpen(false);
    setDealActiveId(null);
  }, []);

  const getOptions: Function = useCallback((
    portfolioIdsOption?: number[],
    portfolioGroupIdsOption?: number[],
    statusOption?: string,
    pageOption?: number,
    allRowsOption?: boolean,
  ): RequestOptions => {
    let options: RequestOptions = {};

    options = portfolioIdsOption && portfolioIdsOption.length
      ? { ...options, portfolioIds: portfolioIdsOption } : options;
    options = portfolioGroupIdsOption && portfolioGroupIdsOption.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsOption } : options;
    options = statusOption ? { ...options, status: statusOption } : options;
    options = pageOption ? { ...options, page: pageOption } : options;
    options = allRowsOption ? { ...options, allRows: allRowsOption } : options;

    return options;
  }, []);

  const processDealFile: Function = useCallback((dealFileItem: DealFileInput): DealFileInput => {
    const resultDealFileItem: DealFileInput = {
      ...dealFileItem,
    };

    resultDealFileItem.accessLevel = resultDealFileItem.accessLevel || null;

    if (dealFileFile) {
      resultDealFileItem.file = dealFileFile;
    }

    if (resultDealFileItem.group) {
      if (Object.keys(resultDealFileItem.group).length) {
        delete resultDealFileItem.groupId;
      } else {
        delete resultDealFileItem.group;
      }
    }

    return resultDealFileItem;
  }, [dealFileFile]);

  const sendDealFileNewItemForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealFileData: DealFileInput | null = dealActiveId ? values.dealListData[dealActiveId].dealFile : null;

    if (!dealFileData) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealListFile({
        dealFile: processDealFile(dealFileData),
        dealId: dealActiveId,
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page, true),
      }, (response: ActionResponse): void => {
        closeDealFileModal();

        onCancelForm();

        setStatusMessage('New file was added successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    dealActiveId, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page,
    dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues,
  ]);

  const onDealFileNewItemSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealFileNewItemForm();
    }
  }, [dealActiveId, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, withFormErrors]);

  const onDealFileSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealFileChange: Function = useCallback((file: File): void => {
    if (dealActiveId) {
      const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealActiveId);

      setDealFileFile(file);

      changeForm(`dealListData[${formKeyDeal}].dealFile.file`, file);
    }
  }, [dealActiveId, dealFileFile]);

  const onDealFileDelete: Function = useCallback((): void => {
    if (dealActiveId) {
      const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealActiveId);

      setDealFileFile(null);

      changeForm(`dealListData[${formKeyDeal}].dealFile.file`, null);
    }
  }, [dealActiveId]);

  const onDealFileCreate: Function = useCallback((): void => {
    if (!isEditMode && dealActiveId) {
      onDealFileNewItemSubmitForm();
    }
  }, [isEditMode, dealActiveId, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus]);

  const onDealFileCancel: Function = useCallback((): void => {
    closeDealFileModal();

    if (!isEditMode) {
      onCancelForm();
    }
  }, [isEditMode, dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const onDealFileCloseNewGroupForm: Function = useCallback((): void => {
    if (dealActiveId) {
      const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealActiveId);

      changeForm(`dealListData[${formKeyDeal}].dealFile.group`, null);
    }
  }, [dealActiveId]);

  const onAddNewDealActivityComment: Function = useCallback((
    activityId: number, comment: string, attachmentFiles: File[], mentionContactIds: number[],
    linkedEntities: CommentEntityInput[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachmentFiles, activityId, mentionContactIds, linkedEntities,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onUpdateNewDealActivityComment: Function = useCallback((
    activityId: number, comment: string, attachments: AttachmentInput[], attachmentFiles: File[], commentId: number,
    mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachments, attachmentFiles, activityId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess(commentId);
      }
    }));
  }, []);

  const onDeleteNewDealActivityComment: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityId: number, commentId: number,
  ): void => {
    dispatch(deleteActivityComment({
      activityId,
      commentId,
    }));

    event.stopPropagation();
  }, []);

  const checkIsDealActive: Function = useCallback((dealId: number): boolean => (
    dealActiveIds ? dealActiveIds.includes(dealId) : false
  ), [dealActiveIds]);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <AddCommentIcon />,
      text: 'Add Comment',
      permission: ACTIVITY_COMMENT_CREATE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealId: number): void => {
        if (dealListData) {
          dealListData.forEach((dealGroupItem: DealStatus): void => {
            if (dealGroupItem.deals && dealGroupItem.deals.length) {
              dealGroupItem.deals.forEach((dealItem: Deal): void => {
                if (dealItem.id === dealId && dealItem.newDealActivity) {
                  openNewDealActivityModal(dealItem.newDealActivity, true);
                }
              });
            }
          });
        }
      },
    },
    {
      id: 2,
      icon: <AttachmentIcon />,
      text: 'Attach File',
      permission: DEAL_FILE_CREATE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealId: number): void => {
        if (dealListData) {
          dealListData.forEach((dealGroupItem: DealStatus): void => {
            if (dealGroupItem.deals && dealGroupItem.deals.length) {
              dealGroupItem.deals.forEach((dealItem: Deal): void => {
                if (dealItem.id === dealId) {
                  openNewDealFileModal(dealId);
                }
              });
            }
          });
        }
      },
    },
    {
      id: 3,
      icon: <ChangeStatusIcon />,
      text: 'Change Status',
      permission: DEAL_STATUS_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealId: number): void => {
        setDealActiveIds([dealId]);
        setIsStatusChangeModalOpen(true);
      },
    },
    {
      id: 4,
      icon: <SendEmailIcon />,
      text: 'Share Deal Info',
      permission: DEAL_VIEW,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealId: number): void => {
        setDealActiveId(dealId);
        setIsShareModalOpen(true);
      },
    },
    {
      id: 5,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealId: number): void => {
        if (dealId) {
          openDeleteConfirmModal(event, dealId);
        }
      },
    },
  ], [dealListData, selectedRows]);

  const controlsActionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <FileUpdateIcon />,
      text: 'Update From File',
      onClick: (): void => {
        openDealListFileUpdateModal();
      },
      permissions: [DEAL_COMMON_EDIT],
    },
  ], []);

  const onCheckIsMenuItemVisible: Function = useCallback((
    actionMenuItem: ActionMenuItem, dealStatus: string,
  ): boolean => (
    (actionMenuItem.isVisibleByEntityTypeValue && !!dealStatus
      && dealStatus === actionMenuItem.isVisibleByEntityTypeValue) || !actionMenuItem.isVisibleByEntityTypeValue
  ), []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onLastUpdateColumnClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityItem: Activity,
  ): void => {
    if (activityItem) {
      openNewDealActivityModal(activityItem);
    }

    event.stopPropagation();
  }, []);

  const onDealStatusColumnClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealId: number, dealName: string | null,
  ): void => {
    if (dealId) {
      openDealStatusChangesModal(dealId, dealName || null);
    }

    event.stopPropagation();
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
      setLastStatusValue(undefined);
      setLastPortfolioIdsValue(undefined);
      setLastPortfolioGroupIdsValue(undefined);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }
    if (isExportMode) {
      dispatch(toggleExportMode(false));
    }
    if (isSelectMode) {
      dispatch(toggleSelectMode(false));
    }
    if (isCustomizeMode) {
      dispatch(toggleCustomizeMode(false));
    }

    dispatch(setStatusFilter(undefined));

    return true;
  }, [isFormChanged, isEditMode, isExportMode, isSelectMode, isCustomizeMode]);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const onInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealId: number,
  ): void => {
    setDealActiveIds([...(dealActiveIds || []), dealId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealActiveIds]);

  const tableDealListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      title: 'Name',
      group: 'dealInformation',
      isSortable: true,
      isNumber: false,
      isRequired: isEditMode,
      isSticky: !isCustomizeMode,
      component: (deal: Deal): ReactElement => (
        <TableCell
          isText
          isSticky={!isCustomizeMode}
          isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)}
        >
          <FlexContainer>
            <Image
              isCentered
              text={deal.name}
              link={deal.mainPhotoThumbnail || deal.mainPhoto || AssetDefaultImage}
            />
            <PaddedFlexContainer>
              <FilteredLink
                link={!isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                  ? `${ROUTE_DEAL}/${deal.id}` : null}
                permission={DEAL_VIEW}
              >
                {deal.name}
              </FilteredLink>
            </PaddedFlexContainer>
          </FlexContainer>
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'asset.portfolio.name',
      title: 'Portfolio',
      group: 'dealInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            (deal.asset && deal.asset.portfolio && deal.asset.portfolio.name)
            || (deal.portfolio && deal.portfolio.name)
          }
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'status',
      title: 'Status',
      group: 'dealInformation',
      isSortable: false,
      isNumber: false,
      isRequired: isEditMode,
      component: (deal: Deal): ReactElement => (
        <TableCell
          isText
          isActiveText={!isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode}
          onClick={(
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
              ? ((event: React.MouseEvent<Element, MouseEvent>): void => {
                onDealStatusColumnClick(event, deal.id, deal.name);
              }) : undefined
          )}
        >
          {deal.statusText}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'rejectReason',
      title: 'Dead Deal Reason',
      group: 'dealInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.rejectReason}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'rejectReasonComment',
      title: 'Dead Deal Reason Comment',
      group: 'dealInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.rejectReasonComment}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'receivedDateValue',
      title: 'Date Received',
      group: 'dealInformation',
      isSortable: true,
      isNumber: true,
      alignLeft: true,
      component: (deal: Deal): ReactElement => (
        <TableCell>
          {deal.receivedDate}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'receivedDateWeekOf',
      title: 'Received Week Of',
      group: 'dealInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell>
          {deal.receivedDateWeekOf}
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'closedDateValue',
      title: 'Date Closed',
      group: 'dealInformation',
      isSortable: true,
      isNumber: true,
      alignLeft: true,
      component: (deal: Deal): ReactElement => (
        <TableCell>
          {deal.closedDate}
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'bidDateValue',
      title: 'Bid Date',
      group: 'dealInformation',
      isSortable: true,
      isNumber: true,
      alignLeft: true,
      component: (deal: Deal): ReactElement => (
        <TableCell>
          {deal.bidDate}
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'sponsors.fullName',
      title: 'Sponsors',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.sponsors && !!deal.sponsors.length && deal.sponsors
              .filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
              .map((contactItem: Contact): ReactElement => (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={contactItem.id}
                  label={contactItem.fullName}
                  imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, contactItem.id);
                  }}
                />
              ))
          }
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'sponsorContacts.fullName',
      title: 'Sponsor Contacts',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.sponsors && !!deal.sponsors.length && deal.sponsors
              .filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
              .map((contactItem: Contact): ReactElement => (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={contactItem.id}
                  label={contactItem.fullName}
                  imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, contactItem.id);
                  }}
                />
              ))
          }
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'brokers.fullName',
      title: 'Brokers',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.brokers && !!deal.brokers.length && deal.brokers
              .filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
              .map((contactItem: Contact): ReactElement => (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={contactItem.id}
                  label={contactItem.fullName}
                  imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, contactItem.id);
                  }}
                />
              ))
          }
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'brokerContacts.fullName',
      title: 'Broker Contacts',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.brokers && !!deal.brokers.length && deal.brokers
              .filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
              .map((contactItem: Contact): ReactElement => (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={contactItem.id}
                  label={contactItem.fullName}
                  imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, contactItem.id);
                  }}
                />
              ))
          }
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'lenders.fullName',
      title: 'Lenders',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.lenders && !!deal.lenders.length && deal.lenders
              .filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
              .map((contactItem: Contact): ReactElement => (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={contactItem.id}
                  label={contactItem.fullName}
                  imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, contactItem.id);
                  }}
                />
              ))
          }
        </TableCell>
      ),
    },
    {
      id: 15,
      name: 'lenderContacts.fullName',
      title: 'Lender Contacts',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.lenders && !!deal.lenders.length && deal.lenders
              .filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
              .map((contactItem: Contact): ReactElement => (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={contactItem.id}
                  label={contactItem.fullName}
                  imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, contactItem.id);
                  }}
                />
              ))
          }
        </TableCell>
      ),
    },
    {
      id: 16,
      name: 'amount',
      title: 'Capitalization',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.amount}
        </TableCell>
      ),
    },
    {
      id: 17,
      name: 'loanAmount',
      title: 'Loan Amount',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.loanAmount}
        </TableCell>
      ),
    },
    {
      id: 18,
      name: 'typeText',
      title: 'Deal Type',
      group: 'dealInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.typeText}
        </TableCell>
      ),
    },
    {
      id: 19,
      name: 'asset.city',
      title: 'City',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.city}
        </TableCell>
      ),
    },
    {
      id: 20,
      name: 'asset.state',
      title: 'State',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.state}
        </TableCell>
      ),
    },
    {
      id: 21,
      name: 'asset.msaLocation',
      title: 'MSA Location',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.msaLocation}
        </TableCell>
      ),
    },
    {
      id: 22,
      name: 'asset.submarket',
      title: 'Submarket',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.submarket}
        </TableCell>
      ),
    },
    {
      id: 23,
      name: 'asset.typesText',
      title: 'Asset Classes',
      group: 'propertyInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.typesText}
        </TableCell>
      ),
    },
    {
      id: 24,
      name: 'newDealActivity.dealSource.name',
      title: 'Deal Source',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.newDealActivity && deal.newDealActivity.dealSource && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={deal.newDealActivity.dealSource.name}
                imageLink={deal.newDealActivity.dealSource.photoThumbnail
                  || deal.newDealActivity.dealSource.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, deal.newDealActivity.dealSource.id);
                }}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 25,
      name: 'newDealActivity.pricing',
      title: 'Price Guidance',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.newDealActivity && deal.newDealActivity.pricing}
        </TableCell>
      ),
    },
    {
      id: 26,
      name: 'newDealActivity.size',
      title: 'Size',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {
            deal.newDealActivity && deal.newDealActivity.size && deal.newDealActivity.sizeType
            && `${deal.newDealActivity.size} ${deal.newDealActivity.sizeType.title}`
          }
        </TableCell>
      ),
    },
    {
      id: 27,
      name: 'newDealActivity.dealContacts.fullName',
      title: 'Deal Contacts',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.newDealActivity && deal.newDealActivity.dealContacts && !!deal.newDealActivity.dealContacts.length
            && deal.newDealActivity.dealContacts.map((contactItem: Contact): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={contactItem.id}
                label={contactItem.fullName}
                imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, contactItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 28,
      name: 'newDealActivity.updatedDate',
      title: 'Last Update',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      alignLeft: true,
      component: (deal: Deal): ReactElement => (
        <TableCell
          isText
          isTextNormal
          onClick={(
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
              ? ((event: React.MouseEvent<Element, MouseEvent>): void => {
                onLastUpdateColumnClick(event, deal.newDealActivity);
              }) : undefined
          )}
        >
          {
            deal.newDealActivity && (
              <LastUpdateBlock
                withNoTooltips
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                userText={deal.newDealActivity.updatedByUserText}
                dateText={deal.newDealActivity.updatedDateText}
                commentList={deal.newDealActivity.comments}
                attachmentList={deal.newDealActivity.attachments}
                commentListViewPermissions={activityCommentListViewPermissions}
                attachmentListViewPermissions={activityAttachmentListViewPermissions}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 29,
      name: 'newDealActivity.lastComment',
      title: 'Last Comment',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => {
        const userComments: Comment[] = deal.newDealActivity && deal.newDealActivity.comments
          ? deal.newDealActivity.comments.filter((commentItem: Comment): boolean => (
            commentItem.type !== COMMENT_TYPE_SYSTEM
          )) : [];
        const lastComment: Comment | null = userComments.length ? userComments[userComments.length - 1] : null;

        return (
          <TableCell
            isText
            isTextNormal
            onClick={(
              !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                ? ((event: React.MouseEvent<Element, MouseEvent>): void => {
                  onLastUpdateColumnClick(event, deal.newDealActivity);
                }) : undefined
            )}
          >
            {
              !!lastComment && (
                <Tooltip
                  withLeftPlacement
                  isInvisible={!lastComment}
                  title={(
                    <FilteredText
                      withMentions
                      withHashtags
                      text={lastComment ? lastComment.text : ''}
                      mentionPermission={CONTACT_VIEW}
                      hashtagPermission={SEARCH_RESULT_VIEW}
                    />
                  )}
                >
                  <FilteredText
                    isLimited
                    withMentions
                    withHashtags
                    text={lastComment ? lastComment.text : ''}
                    mentionPermission={CONTACT_VIEW}
                    hashtagPermission={SEARCH_RESULT_VIEW}
                  />
                </Tooltip>
              )
            }
          </TableCell>
        );
      },
    },
    {
      id: 30,
      name: 'newDealActivity.lastAttachment',
      title: 'Last Attachment',
      group: 'investmentInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => {
        const userCommentWithAttachments: Comment[] = deal.newDealActivity && deal.newDealActivity.comments
          ? deal.newDealActivity.comments.filter((commentItem: Comment): boolean => {
            const lastCommentFileLinkItems: CommentLink[] = commentItem && commentItem.links && commentItem.links.length
              ? commentItem.links.filter((commentLink: CommentLink): boolean => (
                commentLink.type === LINK_TYPE_FILE_LINK
              )) : [];

            return commentItem.type !== COMMENT_TYPE_SYSTEM && !!lastCommentFileLinkItems.length;
          }) : [];
        const lastComment: Comment | null = userCommentWithAttachments.length
          ? userCommentWithAttachments[userCommentWithAttachments.length - 1] : null;
        const lastCommentFileLinks: CommentLink[] = lastComment && lastComment.links && lastComment.links.length
          ? lastComment.links.filter((commentLink: CommentLink): boolean => (
            commentLink.type === LINK_TYPE_FILE_LINK
          )) : [];

        return (
          <TableCell
            isText
            isTextNormal
            onClick={(
              !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                ? ((event: React.MouseEvent<Element, MouseEvent>): void => {
                  onLastUpdateColumnClick(event, deal.newDealActivity);
                }) : undefined
            )}
          >
            {
              lastCommentFileLinks && !!lastCommentFileLinks.length
              && lastCommentFileLinks.map((commentLinkItem: CommentLink): ReactElement => (
                <FileLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  key={commentLinkItem.id}
                  fileLink={commentLinkItem}
                />
              ))
            }
          </TableCell>
        );
      },
    },
    {
      id: 31,
      name: 'dealStructure',
      title: 'Deal Structure',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.dealStructure}
        </TableCell>
      ),
    },
    {
      id: 32,
      name: 'description',
      title: 'Description',
      group: 'dealInformation',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.description}
        </TableCell>
      ),
    },
    {
      id: 33,
      name: 'rfp',
      title: 'RFP',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell>
          {FormatService.formatBooleanValue(deal.rfp, true)}
        </TableCell>
      ),
    },
    {
      id: 34,
      name: 'askingPrice',
      title: 'Asking Price',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.askingPrice}
        </TableCell>
      ),
    },
    {
      id: 35,
      name: 'equityRequired',
      title: 'Required Equity',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.equityRequired}
        </TableCell>
      ),
    },
    {
      id: 36,
      name: 'estimatedTdc',
      title: 'Estimated TDC',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.equityRequired}
        </TableCell>
      ),
    },
    {
      id: 37,
      name: 'asset.sellers.fullName',
      title: 'Sellers',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.asset && deal.asset.sellers && !!deal.asset.sellers.length
            && deal.asset.sellers.map((contactItem: Contact): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={contactItem.id}
                label={contactItem.fullName}
                imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, contactItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 38,
      name: 'asset.assetOwnershipStructure',
      title: 'Ownership Structure',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.assetOwnershipStructureText}
        </TableCell>
      ),
    },
    {
      id: 39,
      name: 'asset.affordableUnits',
      title: 'Affordable Units',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.asset && deal.asset.affordableUnits}
        </TableCell>
      ),
    },
    {
      id: 40,
      name: 'asset.asOfRightZoning',
      title: 'As-of-Right Zoning',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell>
          {deal.asset && FormatService.formatBooleanValue(deal.asset.asOfRightZoning, true)}
        </TableCell>
      ),
    },
    {
      id: 41,
      name: 'loanType',
      title: 'Loan Type',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.loanTypeText}
        </TableCell>
      ),
    },
    {
      id: 42,
      name: 'extensionConditions',
      title: 'Extension Conditions',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {deal.extensionConditions}
        </TableCell>
      ),
    },
    {
      id: 43,
      name: 'equityAmount',
      title: 'Equity Amount',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.equityAmount}
        </TableCell>
      ),
    },
    {
      id: 44,
      name: 'totalUnfunded',
      title: 'Total Unfunded',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.totalUnfunded}
        </TableCell>
      ),
    },
    {
      id: 45,
      name: 'totalFunded',
      title: 'Total Funded',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.totalFunded}
        </TableCell>
      ),
    },
    {
      id: 46,
      name: 'zfaAmount',
      title: 'ZFA',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.zfaAmount}
        </TableCell>
      ),
    },
    {
      id: 47,
      name: 'gsfAmount',
      title: 'GSF',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.gsfAmount}
        </TableCell>
      ),
    },
    {
      id: 48,
      name: 'nsfAmount',
      title: 'NSF',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.nsfAmount}
        </TableCell>
      ),
    },
    {
      id: 49,
      name: 'unitsAmount',
      title: 'Units',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.unitsAmount}
        </TableCell>
      ),
    },
    {
      id: 50,
      name: 'keysAmount',
      title: 'Keys',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.keysAmount}
        </TableCell>
      ),
    },
    {
      id: 51,
      name: 'acresAmount',
      title: 'Acres',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.acresAmount}
        </TableCell>
      ),
    },
    {
      id: 52,
      name: 'lotsfAmount',
      title: 'Lot SF',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.lotsfAmount}
        </TableCell>
      ),
    },
    {
      id: 53,
      name: 'capitalizationSummaryData.sourcesSummary.amount',
      title: 'Total Sources',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.sourcesSummary
            && deal.capitalizationSummaryData.sourcesSummary.amount}
        </TableCell>
      ),
    },
    {
      id: 54,
      name: 'capitalizationSummaryData.usesSummary.amount',
      title: 'Total Uses',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.usesSummary
            && deal.capitalizationSummaryData.usesSummary.amount}
        </TableCell>
      ),
    },
    {
      id: 55,
      name: 'capitalizationSummaryData.sources.debt.amount',
      title: 'Debt, $',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.sources
            && deal.capitalizationSummaryData.sources.debt
            && deal.capitalizationSummaryData.sources.debt.amount}
        </TableCell>
      ),
    },
    {
      id: 56,
      name: 'capitalizationSummaryData.sources.debt.sharePercent',
      title: 'Debt, %',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.sources
            && deal.capitalizationSummaryData.sources.debt
            && deal.capitalizationSummaryData.sources.debt.sharePercent}
        </TableCell>
      ),
    },
    {
      id: 57,
      name: 'capitalizationSummaryData.sources.equity.amount',
      title: 'Equity, $',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.sources
            && deal.capitalizationSummaryData.sources.equity
            && deal.capitalizationSummaryData.sources.equity.amount}
        </TableCell>
      ),
    },
    {
      id: 58,
      name: 'capitalizationSummaryData.sources.equity.sharePercent',
      title: 'Equity, %',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.sources
            && deal.capitalizationSummaryData.sources.equity
            && deal.capitalizationSummaryData.sources.equity.sharePercent}
        </TableCell>
      ),
    },
    {
      id: 59,
      name: 'capitalizationSummaryData.uses.costs.amount',
      title: 'Total Costs, $',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.uses
            && deal.capitalizationSummaryData.uses.costs
            && deal.capitalizationSummaryData.uses.costs.amount}
        </TableCell>
      ),
    },
    {
      id: 60,
      name: 'capitalizationSummaryData.uses.costs.sharePercent',
      title: 'Total Costs, %',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (deal: Deal): ReactElement => (
        <TableCell isNumber>
          {deal.capitalizationSummaryData && deal.capitalizationSummaryData.uses
            && deal.capitalizationSummaryData.uses.costs
            && deal.capitalizationSummaryData.uses.costs.sharePercent}
        </TableCell>
      ),
    },
    {
      id: 61,
      name: 'newDealActivity.tags',
      title: 'Tags',
      group: 'other',
      isSortable: false,
      isNumber: false,
      component: (deal: Deal): ReactElement => (
        <TableCell isText>
          {
            deal.newDealActivity && deal.newDealActivity.tags && !!deal.newDealActivity.tags.length
            && deal.newDealActivity.tags.map((tagItem: Tag): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={tagItem.id}
                label={tagItem.name}
                permission={SEARCH_RESULT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onTagClick(event, tagItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 62,
      name: 'actions',
      title: 'Actions',
      isSortable: false,
      isNumber: false,
      isHeaderNameHidden: true,
      isNotFiltered: true,
      isSticky: !isCustomizeMode && !isExportMode && !isSelectMode,
      isStickyRight: !isCustomizeMode && !isExportMode && !isSelectMode,
      component: (deal: Deal): ReactElement => (
        <TableCell
          isActions
          isTextRight
          isSticky={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyRight={!isCustomizeMode && !isExportMode && !isSelectMode}
          onClick={onActionCellClick}
        >
          {
            (!isEditMode || !checkIsDealActive(deal.id)) && !isExportMode && !isSelectMode && !isCustomizeMode && (
              <CheckAccess permissions={dealCommonEditPermissions}>
                <ActionButton
                  withSmallPadding
                  isHidden={!!isFormSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, deal.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
          {
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode && (
              <CheckAccess
                permissions={actionMenuItems && actionMenuItems
                  .map((actionMenuItem: ActionMenuItem): string | undefined => actionMenuItem.permission)}
              >
                <ActionButton
                  withPopup
                  withSmallPadding
                  isHidden={!!isFormSaving}
                  onClick={openActionMenu}
                  {...{
                    [DATA_DEAL_ID]: deal.id,
                    [DATA_DEAL_STATUS]: deal.status,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
    },
  ], [isEditMode, isExportMode, isSelectMode, isCustomizeMode, dealActiveIds, isFormSaving, actionMenuItems]);

  const tableDealListFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'dealInformation',
      title: 'Deal Information',
    },
    {
      id: 2,
      name: 'investmentInformation',
      title: 'Investment Information',
    },
    {
      id: 3,
      name: 'propertyInformation',
      title: 'Property Information',
    },
    {
      id: 4,
      name: 'other',
      title: 'Other',
    },
  ], []);

  const statusFilterList: StatusFilterItem[] = dealStatusList && dealStatusList.length
    ? dealStatusList.map((dealStatusItem: DealStatus, dealStatusIndex: number): StatusFilterItem => ({
      id: dealStatusIndex + 1,
      name: dealStatusItem.name,
      title: dealStatusItem.title,
      itemsTotal: dealStatusItem.dealsTotal,
    })) : [];

  const inlineCancel: Function = useCallback((): void => {
    const dealId: number | null = inlineSaveDealId;
    if (!dealId) {
      return;
    }

    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    if (initialFormValues && initialFormValues.dealListData) {
      changeForm(`dealListData[${formKeyDeal}]`, initialFormValues.dealListData[formKeyDeal]);
    }

    if (dealId !== FormService.FORM_KEY_NEW) {
      const dealActiveIdIndex: number = dealActiveIds.findIndex((dealItemActiveId: number): boolean => (
        dealItemActiveId === dealId
      ));

      if (dealActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
        const newDealActiveIds: number[] = [...dealActiveIds];
        StructureService.deleteListItemByIndex(newDealActiveIds, dealActiveIdIndex);
        setDealActiveIds(newDealActiveIds);
      }
    }

    setInlineSaveIsConfirm(null);
    setInlineSaveDealId(null);
  }, [initialFormValues, dealActiveIds, inlineSaveDealId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const formData: Record<string, FormValueItem> = {
      ...values.dealListData,
    };

    if (!Object.keys(formData).length) {
      return null;
    }

    const dealListFormData: DealCommonDataInput[] = [];
    Object.keys(formData).forEach((dealIndex: string): void => {
      const dealFormItem: FormValueItem = {
        ...formData[dealIndex],
      };

      if (dealFormItem && ((inlineSaveDealId && inlineSaveDealId === dealFormItem.id)
        || (!inlineSaveDealId && checkIsDealActive(dealFormItem.id)))) {
        dealFormItem.sponsorIds = dealFormItem.sponsorIds && dealFormItem.sponsorIds.length
          ? dealFormItem.sponsorIds.map((contactItem: Contact): number => contactItem.id) : [];

        if (dealFormItem.sponsorContactIds && dealFormItem.sponsorContactIds.length) {
          dealFormItem.sponsorIds = [
            ...dealFormItem.sponsorIds,
            ...(dealFormItem.sponsorContactIds.map((contactItem: Contact): number => contactItem.id)),
          ];
        }

        delete dealFormItem.sponsorContactIds;

        dealFormItem.brokerIds = dealFormItem.brokerIds && dealFormItem.brokerIds.length
          ? dealFormItem.brokerIds.map((contactItem: Contact): number => contactItem.id) : [];

        if (dealFormItem.brokerContactIds && dealFormItem.brokerContactIds.length) {
          dealFormItem.brokerIds = [
            ...dealFormItem.brokerIds,
            ...(dealFormItem.brokerContactIds.map((contactItem: Contact): number => contactItem.id)),
          ];
        }

        delete dealFormItem.brokerContactIds;

        dealFormItem.lenderIds = dealFormItem.lenderIds && dealFormItem.lenderIds.length
          ? dealFormItem.lenderIds.map((contactItem: Contact): number => contactItem.id) : [];

        if (dealFormItem.lenderContactIds && dealFormItem.lenderContactIds.length) {
          dealFormItem.lenderIds = [
            ...dealFormItem.lenderIds,
            ...(dealFormItem.lenderContactIds.map((contactItem: Contact): number => contactItem.id)),
          ];
        }

        delete dealFormItem.lenderContactIds;

        dealFormItem.amount = dealFormItem.amount ? FormatService.formatCleanNumber(dealFormItem.amount) : 0;
        dealFormItem.loanAmount = dealFormItem.loanAmount
          ? FormatService.formatCleanNumber(dealFormItem.loanAmount) : 0;
        dealFormItem.equityRequired = dealFormItem.equityRequired
          ? FormatService.formatCleanNumber(dealFormItem.equityRequired) : 0;
        dealFormItem.estimatedTdc = dealFormItem.estimatedTdc
          ? FormatService.formatCleanNumber(dealFormItem.estimatedTdc) : 0;
        dealFormItem.askingPrice = dealFormItem.askingPrice
          ? FormatService.formatCleanNumber(dealFormItem.askingPrice) : 0;
        dealFormItem.equityAmount = dealFormItem.equityAmount
          ? FormatService.formatCleanNumber(dealFormItem.equityAmount) : 0;
        dealFormItem.totalFunded = dealFormItem.totalFunded
          ? FormatService.formatCleanNumber(dealFormItem.totalFunded) : 0;
        dealFormItem.totalUnfunded = dealFormItem.totalUnfunded
          ? FormatService.formatCleanNumber(dealFormItem.totalUnfunded) : 0;
        dealFormItem.bidDate = dealFormItem.bidDate ? DateService.formatDate(dealFormItem.bidDate) : null;
        dealFormItem.receivedDate = dealFormItem.receivedDate
          ? DateService.formatDate(dealFormItem.receivedDate) : null;
        dealFormItem.closedDate = dealFormItem.closedDate
          ? DateService.formatDate(dealFormItem.closedDate) : null;

        if (dealFormItem.asset) {
          dealFormItem.asset = { ...dealFormItem.asset };
          dealFormItem.asset.sellerIds = dealFormItem.asset.sellerIds && dealFormItem.asset.sellerIds.length
            ? dealFormItem.asset.sellerIds.map((contactItem: Contact): number => contactItem.id) : [];
          dealFormItem.asset.types = dealFormItem.asset.types && dealFormItem.asset.types.length
            ? dealFormItem.asset.types.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
        }

        if (dealFormItem.newDealActivity) {
          dealFormItem.newDealActivity = { ...dealFormItem.newDealActivity };
          dealFormItem.newDealActivity.dealSourceId = dealFormItem.newDealActivity.dealSourceId || null;
          dealFormItem.newDealActivity.dealContactIds = dealFormItem.newDealActivity.dealContactIds
          && dealFormItem.newDealActivity.dealContactIds.length
            ? dealFormItem.newDealActivity.dealContactIds.map((contactItem: Contact): number => contactItem.id) : [];
          dealFormItem.newDealActivity.size = dealFormItem.newDealActivity.size
            ? FormatService.formatCleanNumber(dealFormItem.newDealActivity.size) : 0;
          dealFormItem.newDealActivity.tags = dealFormItem.newDealActivity.tags
          && dealFormItem.newDealActivity.tags.length
            ? dealFormItem.newDealActivity.tags.map((tagItem: Tag): string => tagItem.name) : [];
        }

        delete dealFormItem.dealFile;

        if (dealFormItem.status !== DEAL_STATUS_DRAFT) {
          dealListFormData.push(dealFormItem);
        }
      }
    });

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealListData({
        dealListForm: {
          dealList: dealListFormData,
        },
        ...getOptions(
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          page,
          true,
        ),
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        setInlineSaveDealId(null);
        setInlineSaveIsConfirm(null);

        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSaveDisabled, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, inlineSaveDealId,
    dealActiveIds, page, dealListData, selectedRowsDefault, initialFormValues, isEditMode,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, inlineSaveDealId,
    dealActiveIds, page, withFormErrors, formValues,
  ]);

  const onInlineSave: Function = useCallback((dealId: number): void => {
    setInlineSaveDealId(dealId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onInlineCancel: Function = useCallback((dealId: number): void => {
    setInlineSaveDealId(dealId);
    setInlineSaveIsConfirm(false);
  }, []);

  const openExpansionGroups: Function = useCallback((isDefault?: boolean): void => {
    if (dealListData && dealListData.length) {
      const hiddenDealStatusGroupList: string[] = dealListData
        .filter((dealStatusItem: DealStatus): boolean => dealStatusItem.isCollapsed)
        .map((dealStatusItem: DealStatus): string => dealStatusItem.name);
      setExpandedGroups(dealListData.map((dealStatusItem: DealStatus, dealGroupIndex: number): number | null => (
        ((isDefault && !hiddenDealStatusGroupList.includes(dealStatusItem.name)) || !isDefault)
          ? dealGroupIndex + 1 : null
      )).filter((dealStatusItem: DealStatus): boolean => !!dealStatusItem));
    }
  }, [dealListData]);

  const closeExpansionGroups: Function = useCallback((): void => {
    setExpandedGroups([]);
  }, []);

  const expansionGroupChange: Function = useCallback((groupIndex: number): void => {
    if (expandedGroups) {
      if (expandedGroups.includes(groupIndex)) {
        delete expandedGroups[expandedGroups.indexOf(groupIndex)];
        setExpandedGroups([...expandedGroups.filter((expandedGroup: number): boolean => !!expandedGroup)]);
      } else {
        setExpandedGroups([...expandedGroups, groupIndex]);
      }
    }
  }, [expandedGroups]);

  const onLoadBatchData: Function = useCallback((allRows?: boolean, onSuccess?: Function): void => {
    dispatch(getDealListData(
      getOptions(
        portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page + PAGE_LIST_DEFAULT, allRows || false,
      ),
      (): void => {
        if (onSuccess) {
          onSuccess();
        }
      },
    ));

    setPage(page + PAGE_LIST_DEFAULT);
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page]);

  const onExpansionGroupChange: Function = useCallback((
    event: React.SyntheticEvent, groupIndex: number, withLoading?: boolean,
  ): void => {
    if (withLoading) {
      onLoadBatchData(true, (): void => {
        expansionGroupChange(groupIndex);
      });
    } else {
      expansionGroupChange(groupIndex);
    }

    event.stopPropagation();
  }, [expandedGroups, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page]);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealActiveIds || !dealActiveIds.length) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealList({
        dealIds: dealActiveIds,
        isListItem: true,
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page, true),
      }, (response: ActionResponse): void => {
        setDealActiveIds([]);
        setDealActiveId(null);
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        if (dealListData) {
          dealListData.forEach((dealGroupItem: DealStatus): void => {
            selectedRowsDefault[dealGroupItem.id] = [];
          });
        }

        setSelectedRows(selectedRowsDefault);
        dispatch(toggleSelectMode(false));

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    dealActiveIds, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus,
    dealListData, selectedRowsDefault, page, inlineSaveDealId, initialFormValues,
  ]);

  const removeCreateQueryParam: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    delete queryParams[QUERY_PARAM_CREATE];

    navigate({
      pathname: ROUTE_DEAL_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams]);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setDealActiveIds([]);
    setDealActiveId(null);
    setConfirmModalErrorMessage(null);
  }, []);

  const openDealNewModal: Function = useCallback((): void => {
    setIsDealNewModalOpen(true);
  }, []);

  const closeDealNewModal: Function = useCallback((): void => {
    setIsDealNewModalOpen(false);
  }, []);

  const onDealNewTemplateSelect: Function = useCallback((dealTemplateName: string): void => {
    navigate({
      pathname: `${ROUTE_DEAL}/${ROUTE_PARAM_ID_NEW}`,
      search: FormatService.formatQueryParams({
        [QUERY_PARAM_TEMPLATE]: dealTemplateName,
      }),
    });
  }, []);

  const onDealListFileUpdate: Function = useCallback((
    updateFile: File | null, onSuccess: Function, onError: Function,
  ): Promise<ActionCallback> | null => {
    if (!updateFile) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(updateFileDealList({
        updateFile,
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page, true),
      }, (response: ActionResponse): void => {
        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          if (onError) {
            onError(exception.message);
          }
        }
      }));
    });
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page]);

  const sortTableRows: Function = useCallback((dealGroupId: number, columnName: string, isNumber?: boolean): void => {
    setSortParams({
      ...(sortParams || {}),
      [dealGroupId]: {
        field: columnName,
        type: !!sortParams && !!sortParams[dealGroupId] && sortParams[dealGroupId].field === columnName
        && sortParams[dealGroupId].type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
        isNumber,
      },
    });
  }, [sortParams]);

  const onCreateButtonClick: Function = useCallback((): void => {
    openDealNewModal();
  }, []);

  const onStatusFilterValueChange: Function = useCallback((statusValue: string): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (statusValue) {
      queryParams[QUERY_PARAM_STATUS] = statusValue;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_DEAL_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, dealListData, selectedRowsDefault]);

  const onBeforeStatusFilterValueChange: Function = useCallback((statusValue: string): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastStatusValue(statusValue || null);
      setLastPortfolioGroupIdsValue(undefined);
      setLastPortfolioIdsValue(undefined);
      setLastLocation(undefined);

      return false;
    }

    return true;
  }, [isFormChanged]);

  const onPortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds: number[], portfolioFilterIds: number[],
  ): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioFilterIds && portfolioFilterIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioFilterIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioFilterGroupIds && portfolioFilterGroupIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioFilterGroupIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_DEAL_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, statusFilterStatus]);

  const onBeforePortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds?: number[], portfolioFilterIds?: number[],
  ): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastStatusValue(undefined);
      setLastPortfolioGroupIdsValue(portfolioFilterGroupIds || []);
      setLastPortfolioIdsValue(portfolioFilterIds || []);
      setLastLocation(undefined);

      return false;
    }

    return true;
  }, [isFormChanged]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const openExportModal: Function = useCallback((): void => {
    if (selectedRows) {
      let dealActiveIdList: number[] = [];
      Object.keys(selectedRows).forEach((dealGroupIndex: string): void => {
        const dealItemIds: number[] = selectedRows[dealGroupIndex];

        dealActiveIdList = [...dealActiveIdList, ...dealItemIds];
      });
      setDealActiveIds(dealActiveIdList);

      setIsExportModalOpen(true);
    }
  }, [selectedRows]);

  const closeExportModal: Function = useCallback((): void => {
    setIsExportModalOpen(false);

    onCancelForm();

    closeMessage();
  }, [dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const onExportSuccess: Function = useCallback((): void => {
    onCancelForm();

    setStatusMessage('Exported successfully');
    setIsOpenMessage(true);
  }, [dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const onImportSuccess: Function = useCallback((): void => {
    dispatch(getDealListData(getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus)));
    setPage(PAGE_LIST_DEFAULT);
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus]);

  const onSelectAllGroupRows: Function = useCallback((event: React.ChangeEvent, rowGroupId: number): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    dealListData.forEach((dealGroupItem: DealStatus): void => {
      if (dealGroupItem.id === rowGroupId) {
        setSelectedRows({
          ...selectedRows,
          [rowGroupId]: isSelected ? dealGroupItem.deals.map((dealItem: Deal): number => dealItem.id) : [],
        });
      }
    });

    if (!isSelected) {
      setWithAllRowsSelected(false);
    }
  }, [dealListData, selectedRows]);

  const onSelectRow: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, rowGroupId: number, rowId: number,
  ): void => {
    setSelectedRows({
      ...selectedRows,
      [rowGroupId]: StructureService.selectRow(selectedRows, rowId, rowGroupId),
    });
    setWithAllRowsSelected(false);

    event.stopPropagation();
  }, [selectedRows]);

  const checkIsRowSelected: Function = useCallback((rowGroupId: number, rowId: number): boolean => (
    selectedRows[rowGroupId] ? selectedRows[rowGroupId].includes(rowId) : false
  ), [selectedRows]);

  const checkIsRowsSelected: Function = useCallback((): boolean => {
    let result: boolean = false;
    Object.keys(selectedRows).forEach((dealGroupItemId: string): void => {
      if (selectedRows[dealGroupItemId].length > 0) {
        result = true;
      }
    });

    return result;
  }, [selectedRows]);

  const isExportDisabled: boolean = useMemo((): boolean => (
    !selectedRows || !checkIsRowsSelected()
  ), [selectedRows]);

  const onSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    const dealSelectedRows: Record<string, number[]> = dealListData.reduce((
      resultDealIds: Record<string, number[]>, dealListItem: DealStatus,
    ): Record<string, number[]> => ({
      ...resultDealIds,
      [dealListItem.id]: isSelected ? dealListItem.deals.map((dealItem: Deal): number => dealItem.id) : [],
    }), {});
    setSelectedRows(dealSelectedRows);

    setWithAllRowsSelected(!withAllRowsSelected);
  }, [dealListData, withAllRowsSelected]);

  const onTableColumnSettingsChange: Function = useCallback((
    updatedTableColumnSettings: TableField[],
  ): void => {
    if (updatedTableColumnSettings && updatedTableColumnSettings.length) {
      setTableColumnSettings(updatedTableColumnSettings);

      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_DEAL_LIST,
        tableColumnSettings: updatedTableColumnSettings,
      }));

      setExtendedTableFields(
        StructureService.extendOptions(tableDealListFields, updatedTableColumnSettings),
      );
    }
  }, [tableDealListFields]);

  const onDropColumn: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    if (tableColumnSettings) {
      onTableColumnSettingsChange(StructureService.switchListItems(tableColumnSettings, dragIndex, hoverIndex));
    }
  }, [extendedTableFields, tableColumnSettings, ...tableColumnDropDeps]);

  const onRowClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealGroupId: number, dealId: number, isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isCustomizeMode) {
      if (isExportMode || isSelectMode) {
        onSelectRow(event, dealGroupId, dealId);
      } else if (isEditMode && isListView) {
        const queryParams: LocationQueryParams = {
          [QUERY_PARAM_EDIT_MODE]: isEditMode,
        };

        navigate({
          pathname: `${ROUTE_DEAL}/${dealId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      } else if (!isEditMode) {
        navigate({
          pathname: `${ROUTE_DEAL}/${dealId}`,
        });
      }
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onLongClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealGroupId: number, dealId: number,
  ): void => {
    if (!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode) {
      dispatch(toggleSelectMode(true));
      onSelectRow(event, dealGroupId, dealId);
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onDealSponsorsChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].sponsorIds`, contacts);
  }, []);

  const onDealBrokersChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].brokerIds`, contacts);
  }, []);

  const onDealLendersChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].lenderIds`, contacts);
  }, []);

  const onDealSponsorContactsChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].sponsorContactIds`, contacts);
  }, []);

  const onDealBrokerContactsChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].brokerContactIds`, contacts);
  }, []);

  const onDealLenderContactsChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].lenderContactIds`, contacts);
  }, []);

  const onDealNewDealActivityDealContactsChange: Function = useCallback((dealId: number, contacts: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].newDealActivity.dealContactIds`, contacts);
  }, []);

  const onDealAssetSellersChange: Function = useCallback((dealId: number, contactValues: Contact[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].asset.sellerIds`, contactValues);
  }, []);

  const onDealAssetTypesChange: Function = useCallback((dealId: number, assetTypeValues: AssetType[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].asset.types`, assetTypeValues);
  }, []);

  const onTagsChange: Function = useCallback((dealId: number, tags: Tag[]): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`dealListData[${formKeyDeal}].newDealActivity.tags`, tags);
  }, []);

  const onDealShareClick: Function = useCallback((
    userId: number, onSuccess: Function,
  ): Promise<ActionCallback> | null => {
    if (!userId || !dealActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(shareDealData({
        dealId: dealActiveId,
        userId,
      }, (response: ActionResponse): void => {
        setIsShareModalOpen(false);
        setShareModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Shared Successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setShareModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealActiveId, dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const closeShareModal: Function = useCallback((): void => {
    setIsShareModalOpen(false);
    setDealActiveId(null);
    setShareModalErrorMessage(null);
  }, []);

  const openStatusChangeModal: Function = useCallback((): void => {
    let dealActiveIdList: number[] = [];
    Object.keys(selectedRows).forEach((dealGroupIndex: string): void => {
      const dealItemIds: number[] = selectedRows[dealGroupIndex];

      dealActiveIdList = [...dealActiveIdList, ...dealItemIds];
    });
    setDealActiveIds(dealActiveIdList);

    setIsStatusChangeModalOpen(true);
  }, [selectedRows]);

  const closeStatusModal: Function = useCallback((): void => {
    setStatusModalErrorMessage(null);
    setIsStatusChangeModalOpen(false);

    onCancelForm();

    closeMessage();
  }, [dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const onStatusModalSaveClick: Function = useCallback((
    statusValue: string, rejectReason: string, rejectReasonComment: string, onSuccess: Function,
  ): Promise<ActionCallback> | null => {
    if (!dealActiveIds || !dealActiveIds.length || !statusValue) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealStatusList({
        dealStatusListForm: {
          newStatus: statusValue,
          dealIds: dealActiveIds,
          rejectReason: rejectReason || null,
          rejectReasonComment: rejectReasonComment || null,
        },
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page, true),
      }, (response: ActionResponse): void => {
        setIsStatusChangeModalOpen(false);
        setStatusModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setStatusModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    dealActiveIds, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, dealListData,
    selectedRowsDefault, page, inlineSaveDealId, initialFormValues,
  ]);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        isSmallScreen ? containerRef.current.offsetTop : containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (dealsTotal > commonDealsTotal && !isLoading && !isCustomizeMode) {
            onLoadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [
    containerRef, itemListRef, isSmallScreen, dealsTotal, commonDealsTotal,
    isLoading, isCustomizeMode, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, page,
  ]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    dispatch(setStatusFilter(undefined));

    onCancelForm();
  }, [dealListData, selectedRowsDefault, inlineSaveDealId, dealActiveIds, initialFormValues]);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getDealListData(
      getOptions(
        queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
        queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
          ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
        queryFilterStatus || statusFilterStatus,
      ),
    ));
  }, [
    queryFilterPortfolioIds, queryFilterPortfolioGroupIds, portfolioIdsFilter,
    portfolioGroupIdsFilter, queryFilterStatus, statusFilterStatus,
  ]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (!StructureService.checkIfArraysEqual(portfolioIdsFilter, queryFilterPortfolioIds || undefined)
      || !StructureService.checkIfArraysEqual(portfolioGroupIdsFilter, queryFilterPortfolioGroupIds || undefined)
      || statusFilterStatus !== queryFilterStatus) {
      updateQueryParams();
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));
      setTableColumnSettings(tableColumnSettingsCommon && tableColumnSettingsCommon.value
        && tableColumnSettingsCommon.value[TABLE_NAME_DEAL_LIST]);
    }
  }, [currentUser]);

  useEffect((): void => {
    if (tableColumnSettings && tableColumnSettings.length && !extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(tableDealListFields, tableColumnSettings));
    }
  }, [tableColumnSettings]);

  useEffect((): void => {
    if (extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(
        tableDealListFields, tableColumnSettings, isEditMode,
      ));
    }
  }, [isEditMode, isExportMode, isSelectMode, isCustomizeMode, dealActiveIds, isFormSaving]);

  useEffect((): void => {
    openExpansionGroups(true);
  }, [isDataLoaded]);

  useEffect((): void => {
    if (!isDataLoaded) {
      setPage(PAGE_LIST_DEFAULT);

      if (queryFilterStatus) {
        dispatch(setStatusFilter(queryFilterStatus));
      }

      if (queryFilterPortfolioIds && queryFilterPortfolioIds.length) {
        dispatch(setPortfolioIdsFilter(queryFilterPortfolioIds));
      }

      if (queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length) {
        dispatch(setPortfolioGroupIdsFilter(queryFilterPortfolioGroupIds));

        if (!queryFilterPortfolioIds || !queryFilterPortfolioIds.length) {
          dispatch(setPortfolioIdsFilter(undefined));
        }
      }

      dispatch(getDealListData(
        getOptions(
          queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
          queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
            ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
          queryFilterStatus || statusFilterStatus,
        ),
      ));
    }
  }, [isDataLoaded]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getDealListData(getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus)));
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastStatusValue || lastStatusValue === null) {
        dispatch(setStatusFilter(lastStatusValue));

        setLastStatusValue(undefined);
      }

      if (lastPortfolioIdsValue) {
        dispatch(setPortfolioIdsFilter(lastPortfolioIdsValue));

        setLastPortfolioIdsValue(undefined);
      }

      if (lastPortfolioGroupIdsValue) {
        dispatch(setPortfolioGroupIdsFilter(lastPortfolioGroupIdsValue));

        setLastPortfolioGroupIdsValue(undefined);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (dealListData && dealListData.length) {
      dealListData.forEach((dealGroupItem: DealStatus): void => {
        if (dealGroupItem.deals && dealGroupItem.deals.length) {
          dealGroupItem.deals.forEach((dealItem: Deal): void => {
            if (dealItem.id === dealActiveId) {
              setDealFileGroupList(dealItem.dealFileGroupList || []);
            }
          });
        }
      });
    }
  }, [dealActiveId]);

  useEffect((): void => {
    if (dealStatusList) {
      let initialStatusFilterText: string | null = null;
      dealStatusList.forEach((dealStatusItem: DealStatus): void => {
        if (dealStatusItem.name === statusFilterStatus) {
          initialStatusFilterText = dealStatusItem.title;
        }
      });
      setStatusFilterText(initialStatusFilterText);
    }
  }, [dealStatusList]);

  useEffect((): void => {
    if (dealListData) {
      const initialSortParams: Record<string, SortParams> = sortParams || {};
      dealListData.forEach((dealGroupItem: DealStatus): void => {
        if (!initialSortParams[dealGroupItem.id]) {
          initialSortParams[dealGroupItem.id] = DEFAULT_SORT;
        }
      });
      setSortParams(initialSortParams);

      const initialRowsSelectedDefault: Record<string, number[]> = {};
      const initialRowsSelected: Record<string, number[]> = {};
      dealListData.forEach((dealGroupItem: DealStatus): void => {
        initialRowsSelectedDefault[dealGroupItem.id] = [];
        initialRowsSelected[dealGroupItem.id] = (selectedRows && selectedRows[dealGroupItem.id]) || [];
      });
      setSelectedRowsDefault(initialRowsSelectedDefault);
      setSelectedRows(initialRowsSelected);
    }
  }, [dealListData]);

  useEffect((): void => {
    if (dealListData && newDealActivityItemData) {
      dealListData.forEach((dealGroupItem: DealStatus): void => {
        if (dealGroupItem.deals && dealGroupItem.deals.length) {
          dealGroupItem.deals.forEach((dealItem: Deal): void => {
            if (dealItem.newDealActivity && dealItem.newDealActivity.id === newDealActivityItemData.id) {
              setNewDealActivityItemData({ ...dealItem.newDealActivity });
            }
          });
        }
      });
    }
  }, [newCommentList]);

  useEffect((): void => {
    if (inlineSaveIsConfirm !== null && inlineSaveDealId !== null) {
      if (inlineSaveIsConfirm) {
        onSubmitForm();
      } else {
        onCancelForm();
      }
    }
  }, [inlineSaveIsConfirm]);

  useEffect((): void => {
    if (queryCreate) {
      openDealNewModal();
      removeCreateQueryParam();
    }
  }, [queryCreate]);

  useEffect((): void => {
    if (withServerErrors && inlineSaveIsConfirm !== null) {
      setInlineSaveDealId(null);
      setInlineSaveIsConfirm(null);
    }
  }, [withServerErrors]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useScroll(onScroll, scrollDeps);

  useArrowNavigation(inputRefs);

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!isDataLoaded || !tableColumnSettings) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={dealListViewPermissions}>
      <Form id={FORM_ID_DEAL_LIST} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader
            title={pageTitle}
            leftBlockComponent={isPortfolioFilterViewAllowed && !!portfolioGroupList && !isSmallScreen && (
              <PortfolioFilter
                isDisabled={isFormChanged}
                portfolioGroupList={portfolioGroupList}
                onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
                onBeforePortfolioFilterValuesChange={onBeforePortfolioFilterValuesChange}
              />
            )}
          />
          {
            isSmallScreen && (
              <CheckAccess permissions={dealListPortfolioFilterViewPermissions}>
                <PortfolioFilter
                  isInline
                  isDisabled={isFormChanged}
                  portfolioGroupList={filteredPortfolioGroupList}
                  onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
                  onBeforePortfolioFilterValuesChange={onBeforePortfolioFilterValuesChange}
                />
              </CheckAccess>
            )
          }
          <CheckAccess permissions={dealListViewPermissions}>
            <StatusFilter
              isDisabled={isFormChanged}
              statusFilterStatus={statusFilterStatus}
              statusFilterList={statusFilterList}
              onStatusFilterValueChange={onStatusFilterValueChange}
              onBeforeStatusFilterValueChange={onBeforeStatusFilterValueChange}
            />
          </CheckAccess>
          <Controls
            withGroups
            isTableExport
            withSelectAll
            withSelectMode
            withColumnSettings
            isSaveDisabled={isSaveDisabled}
            isCustomizable={isCustomizable}
            isCustomizeMode={isCustomizeMode}
            isGroupExpanded={isGroupExpanded}
            isExportDisabled={isExportDisabled}
            withAllRowsSelected={withAllRowsSelected}
            createButtonTitle={createButtonTitle}
            createPermission={DEAL_CREATE}
            exportFormatList={FORMAT_LIST_EXPORT_DEAL}
            importFormatList={FORMAT_LIST_IMPORT_DEAL}
            importExportEntityType={IMPORT_EXPORT_ENTITY_TYPE_DEAL_LIST}
            importPermission={DEAL_LIST_IMPORT}
            exportPermission={DEAL_LIST_EXPORT}
            editPermissions={dealCommonEditPermissions}
            changeStatusPermission={DEAL_STATUS_EDIT}
            deletePermission={DEAL_DELETE}
            tableFields={tableDealListFields}
            tableFieldGroups={tableDealListFieldGroups}
            tableColumnSettings={tableColumnSettings}
            customActionMenuItems={controlsActionMenuItems}
            rowsTotal={dealsTotal}
            rowsSelectedTotal={dealsSelectedTotal}
            selectedItemIds={selectedRows}
            status={statusFilterStatus}
            portfolioIds={portfolioIdsFilter}
            portfolioGroupIds={portfolioGroupIdsFilter}
            onCancelForm={onCancelForm}
            onSubmitForm={onSubmitForm}
            onExportClick={openExportModal}
            onDeleteClick={openDeleteConfirmModal}
            onExpandAll={openExpansionGroups}
            onCollapseAll={closeExpansionGroups}
            onImportSuccess={onImportSuccess}
            onSelectAllRows={onSelectAllRows}
            onTableColumnsChange={onTableColumnSettingsChange}
            onCreateButtonClick={onCreateButtonClick}
            onStatusChangeClick={openStatusChangeModal}
          />
          {
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode && dealAssetLocationItemList
            && !!dealAssetLocationItemList.length && (
              <ExpansionGroupWidget headerName="Deal Locations" permission={DEAL_LIST_DEAL_LOCATIONS_VIEW}>
                <LocationMap
                  defaultColor={MARKER_COLOR_RED}
                  colorIdList={portfolioMarkerColorIdList}
                  locationItemList={dealAssetLocationItemList}
                />
              </ExpansionGroupWidget>
            )
          }
          {
            dealListData && !!dealListData.length && extendedTableFields && !!extendedTableFields.length && (
              <Card>
                <ContentListContainerOffset
                  isDefault={isSmallScreen}
                  withMap={withMap}
                  withButtons={withButtons}
                  withStatusFilter={withStatusFilter}
                  ref={containerRef}
                  onScroll={!isSmallScreen ? onScroll : undefined}
                >
                  <div ref={itemListRef}>
                    {
                      dealListData.map((dealStatus: DealStatus, dealGroupIndex: number): ReactElement | boolean => {
                        const dealStatusDealsTotal: number = dealStatus ? dealStatus.deals.length : 0;
                        const dealStatusDealsSelectedTotal: number = selectedRows
                        && selectedRows[dealStatus.id] ? selectedRows[dealStatus.id].length : 0;
                        const isDealStatusSelected: boolean = withAllRowsSelected
                          || dealStatusDealsSelectedTotal === dealStatusDealsTotal;
                        const isDealStatusIndeterminate: boolean = !withAllRowsSelected
                          && dealStatusDealsSelectedTotal > 0
                          && dealStatusDealsSelectedTotal < dealStatusDealsTotal;
                        const visibleTableFieldsTotal: number = extendedTableFields
                          .filter((tableField: TableField): boolean => !!tableField.isVisible).length;
                        const withGroupItems: boolean = dealStatus.deals && !!dealStatus.deals.length;
                        const isGroupLoading: boolean = !!dealStatus.dealsTotal
                          && dealStatus.dealsTotal > dealStatusDealsTotal;
                        const isGroupCollapsed: boolean = expandedGroups
                          && !expandedGroups.includes(dealGroupIndex + 1);
                        const isCurrentTableLoading: boolean = isTableLoading;
                        const isGroupRendered: boolean = ((!!dealStatus.dealsTotal && !!dealStatusDealsTotal)
                          || !dealStatus.dealsTotal) && !isCurrentTableLoading;
                        const isVisible: boolean = (!isExportMode && !isSelectMode && !isEditMode)
                          || ((isExportMode || isSelectMode || isEditMode) && (withGroupItems || isGroupLoading));

                        isTableLoading = isTableLoading || isGroupLoading;

                        return (isGroupRendered || isGroupCollapsed) && (
                          <ExpansionGroup
                            isTableGroup
                            isExpanded={(isSmallScreen && !isGroupCollapsed) || !isSmallScreen}
                            withNoHeader={!isSmallScreen}
                            key={dealStatus.id}
                            onChange={(event: React.SyntheticEvent): void => {
                              onExpansionGroupChange(event, dealGroupIndex + 1, !isGroupRendered);
                            }}
                            headerComponent={isSmallScreen && (
                              <FlexContainer isSpread isStart>
                                {dealStatus.title}
                              </FlexContainer>
                            )}
                          >
                            {
                              !isSmallScreen && (
                                <TableResize
                                  isActive={isCustomizeMode}
                                  isDisabled={isEditMode}
                                  groupId={dealStatus.id}
                                  tableRef={tableRefs}
                                  headerCellRefs={headerCellRefs}
                                  tableColumnSettings={tableColumnSettings}
                                  onTableColumnsChange={onTableColumnSettingsChange}
                                >
                                  <HiddenBlock isActive={!isVisible}>
                                    <Table
                                      withStickyHeader
                                      isFullWidth={isEditMode}
                                      ref={(ref: HTMLTableElement): void => {
                                        if (tableRefs.current) {
                                          tableRefs.current[dealStatus.id] = ref;
                                        }
                                      }}
                                    >
                                      <TableHead isSubHeader>
                                        {
                                          !isSmallScreen && (
                                            <TableRowGroupName
                                              isButton
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onExpansionGroupChange(event, dealGroupIndex + 1, !isGroupRendered);
                                              }}
                                            >
                                              {
                                                (isExportMode || isSelectMode) && (
                                                  <TableCell
                                                    isSticky={!isCustomizeMode}
                                                    isStickyLeft={!isCustomizeMode}
                                                    isStickyGroupLeft={!isCustomizeMode}
                                                  />
                                                )
                                              }
                                              <TableCell
                                                isSticky={!isCustomizeMode}
                                                isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode}
                                                isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)}
                                                isStickyGroupLeft={!isCustomizeMode && !isExportMode && !isSelectMode}
                                                isStickyGroupLeftSelect={!isCustomizeMode
                                                  && (isExportMode || isSelectMode)}
                                                colSpan={isCustomizeMode ? 1 : 3}
                                              >
                                                {dealStatus.title}
                                              </TableCell>
                                              <TableCell
                                                isSticky={!isCustomizeMode}
                                                colSpan={visibleTableFieldsTotal - (isCustomizeMode ? 1 : 3)}
                                              />
                                              <TableCell
                                                isActions
                                                isTextRight
                                                isSticky={!isCustomizeMode}
                                                isStickyRight={!isCustomizeMode}
                                                isStickyGroupRight={!isCustomizeMode}
                                              >
                                                <ActionButtonContainer>
                                                  {
                                                    !isSmallScreen && (
                                                      <ActionButton
                                                        isLight
                                                        withNoPadding
                                                        isDisabled={!isGroupRendered}
                                                        onClick={(
                                                          event: React.MouseEvent<Element, MouseEvent>,
                                                        ): void => {
                                                          onExpansionGroupChange(
                                                            event,
                                                            dealGroupIndex + 1,
                                                            !isGroupRendered,
                                                          );
                                                        }}
                                                      >
                                                        {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                      </ActionButton>
                                                    )
                                                  }
                                                </ActionButtonContainer>
                                              </TableCell>
                                            </TableRowGroupName>
                                          )
                                        }
                                        <TableRowGroupHeader
                                          isSticky
                                          isHidden={!isSmallScreen && isGroupCollapsed}
                                        >
                                          {
                                            (isExportMode || isSelectMode) && (
                                              <TableCell isSticky isStickyLeft>
                                                <Checkbox
                                                  isHeader
                                                  isChecked={isDealStatusSelected}
                                                  isIndeterminate={isDealStatusIndeterminate}
                                                  id={`${ID_PREFIX_CHECKBOX_EXPORT_GROUP}-${dealStatus.id}`}
                                                  onChange={(event: React.ChangeEvent): void => {
                                                    onSelectAllGroupRows(event, dealStatus.id);
                                                  }}
                                                />
                                              </TableCell>
                                            )
                                          }
                                          {
                                            extendedTableFields.map((
                                              tableField: TableField, tableFieldIndex: number,
                                            ): ReactElement => (
                                              <FilteredCell
                                                key={tableField.id}
                                                columnName={tableField.name}
                                                tableName={TABLE_NAME_DEAL_LIST}
                                                currentUser={currentUser}
                                              >
                                                <TableCell
                                                  isNumber={tableField.isNumber && !tableField.alignLeft && !isEditMode}
                                                  isSticky={!isCustomizeMode && tableField.isSticky}
                                                  isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode
                                                    && tableField.isSticky && !tableField.isStickyRight}
                                                  isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)
                                                    && tableField.isSticky && !tableField.isStickyRight}
                                                  isStickyRight={!isCustomizeMode && tableField.isSticky
                                                    && tableField.isStickyRight}
                                                  isStickyRightHeader={!isCustomizeMode
                                                    && !isExportMode && !isSelectMode
                                                    && tableField.isSticky && tableField.isStickyRight}
                                                  ref={(ref: HTMLElement): void => {
                                                    if (headerCellRefs.current) {
                                                      if (!headerCellRefs.current[dealStatus.id]) {
                                                        headerCellRefs.current[dealStatus.id] = {};
                                                      }

                                                      headerCellRefs.current[dealStatus.id][tableFieldIndex] = ref;
                                                    }
                                                  }}
                                                  sortDirection={(
                                                    sortParams && sortParams[dealStatus.id] && tableField.isSortable
                                                    && sortParams[dealStatus.id].field === tableField.name
                                                      ? sortParams[dealStatus.id].type : false
                                                  )}
                                                >
                                                  {
                                                    tableField.isSortable && !isEditMode ? (
                                                      <DraggableItem
                                                        isDisabled={!isCustomizeMode}
                                                        itemId={tableField.id}
                                                        index={tableFieldIndex}
                                                        dropDeps={tableColumnDropDeps}
                                                        onDropItem={onDropColumn}
                                                      >
                                                        <TableSortLabel
                                                          withNoLabel={tableField.isHeaderNameHidden}
                                                          isActive={sortParams && sortParams[dealStatus.id]
                                                            ? sortParams[dealStatus.id].field === tableField.name
                                                            : false}
                                                          sortType={sortParams && sortParams[dealStatus.id]
                                                            ? sortParams[dealStatus.id].type : SORT_TYPE_ASC}
                                                          onClick={(): void => {
                                                            sortTableRows(
                                                              dealStatus.id, tableField.name, tableField.isNumber,
                                                            );
                                                          }}
                                                        >
                                                          {tableField.title}
                                                        </TableSortLabel>
                                                      </DraggableItem>
                                                    ) : (
                                                      !tableField.isHeaderNameHidden && (
                                                        <DraggableItem
                                                          isDisabled={!isCustomizeMode}
                                                          itemId={tableField.id}
                                                          index={tableFieldIndex}
                                                          dropDeps={tableColumnDropDeps}
                                                          onDropItem={onDropColumn}
                                                        >
                                                          {tableField.title}
                                                          {tableField.isRequired && <RequiredMark />}
                                                        </DraggableItem>
                                                      )
                                                    )
                                                  }
                                                </TableCell>
                                              </FilteredCell>
                                            ))
                                          }
                                        </TableRowGroupHeader>
                                      </TableHead>
                                      <TableBody
                                        isPlain
                                        withZebraRows
                                        isHidden={!isSmallScreen && isGroupCollapsed}
                                        withBorderedCells={!isCustomizeMode}
                                        withFixedCellsSizeExtended={isEditMode}
                                      >
                                        {
                                          dealStatus.deals && !!dealStatus.deals.length
                                          && sortParams && sortParams[dealStatus.id]
                                          && SortService.sort(dealStatus.deals, sortParams[dealStatus.id])
                                            .map((deal: Deal): ReactElement => {
                                              const isSelected: boolean = selectedRows
                                                ? withAllRowsSelected || checkIsRowSelected(dealStatus.id, deal.id)
                                                : false;
                                              const isActive: boolean = checkIsDealActive(deal.id);
                                              const isInlineSaving: boolean = inlineSaveDealId !== null
                                                ? deal.id === inlineSaveDealId : isFormSaving;
                                              const commonDealIndex: number = commonRowIndex;
                                              commonRowIndex++;

                                              return (
                                                <DealRow
                                                  isActive={isActive}
                                                  isSaving={isFormSaving}
                                                  isSaveDisabled={isSaveDisabled}
                                                  isInlineSaving={isInlineSaving}
                                                  isEditMode={isEditMode}
                                                  isExportMode={isExportMode}
                                                  isSelectMode={isSelectMode}
                                                  isCustomizeMode={isCustomizeMode}
                                                  isSelected={isSelected}
                                                  key={deal.id}
                                                  inputRefs={inputRefs}
                                                  currentUser={currentUser}
                                                  dealIndex={commonDealIndex}
                                                  dealItem={deal}
                                                  dealGroupItem={dealStatus}
                                                  tableName={TABLE_NAME_DEAL_LIST}
                                                  tableDealListFields={extendedTableFields}
                                                  tableColumnSettings={tableColumnSettings}
                                                  assetTypeList={assetTypeList}
                                                  dealTypeList={dealTypeList}
                                                  dealStatusList={filteredDealStatusList}
                                                  contactCompanyList={contactCompanyList}
                                                  contactPersonList={contactPersonList}
                                                  contactPersonInternalList={contactPersonInternalList}
                                                  measureUnitList={measureUnitList}
                                                  assetOwnershipStructureTypeList={assetOwnershipStructureTypeList}
                                                  dealLoanTypeList={dealLoanTypeList}
                                                  tagList={tagList}
                                                  onRowClick={onRowClick}
                                                  onLongClick={onLongClick}
                                                  onInlineSave={onInlineSave}
                                                  onInlineCancel={onInlineCancel}
                                                  onTagsChange={onTagsChange}
                                                  onDealAssetTypesChange={onDealAssetTypesChange}
                                                  onDealAssetSellersChange={onDealAssetSellersChange}
                                                  onDealSponsorsChange={onDealSponsorsChange}
                                                  onDealBrokersChange={onDealBrokersChange}
                                                  onDealLendersChange={onDealLendersChange}
                                                  onDealSponsorContactsChange={onDealSponsorContactsChange}
                                                  onDealBrokerContactsChange={onDealBrokerContactsChange}
                                                  onDealLenderContactsChange={onDealLenderContactsChange}
                                                  onDealNewDealActivityDealContactsChange={(
                                                    onDealNewDealActivityDealContactsChange
                                                  )}
                                                  onContactPersonSearch={onContactPersonSearch}
                                                  onContactPersonSearchClose={onContactPersonSearchClose}
                                                  onContactCompanySearch={onContactCompanySearch}
                                                  onContactCompanySearchClose={onContactCompanySearchClose}
                                                  onContactPersonInternalSearch={onContactPersonInternalSearch}
                                                  onContactPersonInternalSearchClose={(
                                                    onContactPersonInternalSearchClose
                                                  )}
                                                  onTagSearch={onTagSearch}
                                                  onTagSearchClose={onTagSearchClose}
                                                />
                                              );
                                            })
                                        }
                                      </TableBody>
                                    </Table>
                                  </HiddenBlock>
                                </TableResize>
                              )
                            }
                            {
                              isSmallScreen && (
                                <List>
                                  {
                                    dealStatus.deals.map((deal: Deal): ReactElement => {
                                      const isSelected: boolean = selectedRows ? (withAllRowsSelected
                                        || checkIsRowSelected(dealStatus.id, deal.id)) : false;

                                      return (
                                        <LongClickListItem
                                          isButton={!isSelectMode && !isExportMode}
                                          isActive={isSelected}
                                          key={deal.id}
                                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                            onRowClick(event, dealStatus.id, deal.id, true);
                                          }}
                                          onLongClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                            onLongClick(event, dealStatus.id, deal.id);
                                          }}
                                        >
                                          {
                                            (isExportMode || isSelectMode) && (
                                              <ListItemCheckbox isChecked={isSelected} itemId={deal.id} />
                                            )
                                          }
                                          <ListItemImage
                                            text={deal.name}
                                            link={deal.mainPhotoThumbnail || deal.mainPhoto || AssetDefaultImage}
                                          />
                                          <ListItemText isBlock>
                                            {deal.name}
                                          </ListItemText>
                                          <ListItemText
                                            isBlock
                                            isBlockRight
                                            subText={!isEditMode ? deal.amount : (
                                              <FlexContainer isEnd>
                                                <ActionButton
                                                  withSmallPadding
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    onRowClick(event, dealStatus.id, deal.id, true);
                                                  }}
                                                >
                                                  <ArrowLinkIcon />
                                                </ActionButton>
                                              </FlexContainer>
                                            )}
                                          />
                                        </LongClickListItem>
                                      );
                                    })
                                  }
                                </List>
                              )
                            }
                          </ExpansionGroup>
                        );
                      })
                    }
                  </div>
                  {
                    isLoading && isDataLoaded && (
                      <Loader isInline />
                    )
                  }
                </ContentListContainerOffset>
              </Card>
            )
          }
          {
            dealListData && !dealListData.length && (
              <NoContentBlock>
                No deals found by your request
              </NoContentBlock>
            )
          }
          <div>
            <ControlButtonBlock
              isEditMode={isEditMode}
              isExportMode={isExportMode}
              isSelectMode={isSelectMode}
              isSaveDisabled={isSaveDisabled}
              isExportDisabled={isExportDisabled}
              withOffset={isRightDrawerOpen}
              onCancelForm={onCancelForm}
              onSubmitForm={onSubmitForm}
              onExportClick={openExportModal}
            />
            {
              (!!formError || !!mainServerFormError) && !isFormSaving && (
                <FormError
                  withShowError={isEditMode}
                  withLeftOffset={isRightDrawerOpen}
                  error={formError || mainServerFormError}
                />
              )
            }
            {
              isFormSaving && isEditMode && (
                <Loader isInline isSubmit isFloating isHidden={isMobileScreen} />
              )
            }
          </div>
        </PullToRefresh>
        <ActivityModal
          isDealActivity
          isOpen={isNewDealActivityModalOpen}
          isSaving={isFormSaving}
          isCommentSaving={isActivityCommentSaving}
          isCommentFormActive={isCommentFormActive}
          currentUser={currentUser}
          newCommentList={newCommentList}
          activityData={newDealActivityItemData}
          tagList={tagList}
          contactCompanyList={contactCompanyList}
          contactPersonList={contactPersonList}
          saveFormDeps={saveFormDeps}
          contactLinkedUserList={contactLinkedUserList}
          onCloseModal={closeNewDealActivityModal}
          onSubmitCreateForm={onNewDealActivitySubmitCreateForm}
          onAddComment={onAddNewDealActivityComment}
          onUpdateComment={onUpdateNewDealActivityComment}
          onDeleteComment={onDeleteNewDealActivityComment}
          onActivityDealSearch={onActivityDealSearch}
          onActivityDealSearchClose={onActivityDealSearchClose}
          onDealAssetSearch={onDealAssetSearch}
          onDealAssetSearchClose={onDealAssetSearchClose}
          onContactSearch={onContactSearch}
          onContactSearchClose={onContactSearchClose}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
        />
        <DealFileModal
          isNewEntity
          isDealListModal
          isOpen={isDealFileModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          dealId={dealActiveId}
          dealFileFile={dealFileFile}
          accessLevelList={accessLevelList}
          dealFileGroupList={dealFileGroupList}
          saveFormDeps={saveFormDeps}
          onCreate={onDealFileCreate}
          onCancel={onDealFileCancel}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCloseModal={closeDealFileModal}
          onFileChange={onDealFileChange}
          onFileDelete={onDealFileDelete}
          onSubmitCreateForm={onDealFileSubmitCreateForm}
          onCloseNewGroupForm={onDealFileCloseNewGroupForm}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          withExportAll={withAllRowsSelected}
          portfolioIds={portfolioIdsFilter}
          portfolioGroupIds={portfolioGroupIdsFilter}
          entityIds={dealActiveIds}
          exportEntityType={IMPORT_EXPORT_ENTITY_TYPE_DEAL_LIST}
          status={statusFilterStatus}
          exportFormatList={FORMAT_LIST_EXPORT_DEAL}
          onSuccess={onExportSuccess}
          onCloseModal={closeExportModal}
        />
        <DealNewModal
          isOpen={isDealNewModalOpen}
          dealTemplateList={dealTemplateList}
          onConfirm={onDealNewTemplateSelect}
          onCloseModal={closeDealNewModal}
        />
        <FileUpdateModal
          isOpen={isDealListFileUpdateModalOpen}
          isSaving={isFormSaving}
          updateFileFormatList={FORMAT_LIST_IMPORT_DEAL}
          onConfirm={onDealListFileUpdate}
          onCloseModal={closeDealListFileUpdateModal}
        />
        <ShareModal
          isOpen={isShareModalOpen}
          title={shareModalTitle}
          description={shareModalDescription}
          contactLinkedUserList={contactLinkedUserList}
          errorMessage={shareModalErrorMessage}
          onShareClick={onDealShareClick}
          onCloseModal={closeShareModal}
        />
        <DealStatusChangesModal
          isOpen={isDealStatusChangesModalOpen}
          isLoading={isDealStatusChangeListLoading}
          dealName={dealStatusChangesModalDealName}
          errorMessage={dealStatusChangesModalErrorMessage}
          onCloseModal={closeDealStatusChangesModal}
          dealStatusChangeListData={dealStatusChangeListData}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <StatusModal
          isOpen={isStatusChangeModalOpen}
          isSaving={isFormSaving}
          statusList={filteredCommonDealStatusList}
          reasonStatusList={REASON_STATUS_LIST}
          statusChangeReasonList={dealRejectReasonList}
          itemActiveIds={dealActiveIds}
          errorMessage={statusModalErrorMessage}
          onCloseModal={closeStatusModal}
          onSaveForm={onStatusModalSaveClick}
        />
        <ContextMenu
          menuAnchor={menuAnchor}
          actionMenuItems={actionMenuItems}
          dataAttributeEntityId={DATA_DEAL_ID}
          dataAttributeEntityType={DATA_DEAL_STATUS}
          onClose={closeActionMenu}
          onCheckIsMenuItemVisible={onCheckIsMenuItemVisible}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    dealList,
    portfolioFilter,
    statusFilter,
    rightDrawer,
    controls,
    deal,
    activityList,
    activity,
  } = reducers;

  const dealListData: DealStatus[] = dealList.dealListData && dealList.dealListData.dealList;

  if (dealListData) {
    initialValues.dealListData = {};

    dealListData.forEach((dealStatus: DealStatus): void => {
      if (dealStatus.deals && dealStatus.deals.length) {
        dealStatus.deals.forEach((dealItem: Deal): void => {
          const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealItem.id);

          initialValues.dealListData[formKeyDeal] = {
            id: dealItem.id,
            name: dealItem.name,
            status: dealItem.status,
            type: dealItem.type,
            sponsorIds: dealItem.sponsors && dealItem.sponsors.length
              ? dealItem.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
                .map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
            sponsorContactIds: dealItem.sponsors && dealItem.sponsors.length
              ? dealItem.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
                .map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
            brokerIds: dealItem.brokers && dealItem.brokers.length
              ? dealItem.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
                .map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
            brokerContactIds: dealItem.brokers && dealItem.brokers.length
              ? dealItem.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
                .map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
            lenderIds: dealItem.lenders && dealItem.lenders.length
              ? dealItem.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
                .map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
            lenderContactIds: dealItem.lenders && dealItem.lenders.length
              ? dealItem.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
                .map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
            receivedDate: dealItem.receivedDate ? DateService.parseDate(dealItem.receivedDate) : null,
            closedDate: dealItem.closedDate ? DateService.parseDate(dealItem.closedDate) : null,
            bidDate: dealItem.bidDate ? DateService.parseDate(dealItem.bidDate) : null,
            amount: FormatService.formatNumber(dealItem.amount),
            loanAmount: FormatService.formatNumber(dealItem.loanAmount),
            asset: dealItem.asset ? {
              id: dealItem.asset.id,
              name: dealItem.asset.name,
              status: dealItem.asset.status,
              portfolioId: dealItem.asset.portfolio ? dealItem.asset.portfolio.id : null,
              types: dealItem.asset.types || [],
              assetOwnershipStructure: dealItem.asset.assetOwnershipStructure,
              affordableUnits: dealItem.asset.affordableUnits,
              asOfRightZoning: dealItem.asset.asOfRightZoning,
              sellerIds: dealItem.asset.sellers && dealItem.asset.sellers.length
                ? dealItem.asset.sellers.map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  name: contactItem.name,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
              submarket: dealItem.asset.submarket,
            } : null,
            portfolioId: dealItem.portfolio ? dealItem.portfolio.id : null,
            newDealActivity: dealItem.newDealActivity ? {
              id: dealItem.newDealActivity.id,
              dealSourceId: dealItem.newDealActivity.dealSource ? dealItem.newDealActivity.dealSource.id : null,
              pricing: dealItem.newDealActivity.pricing,
              size: FormatService.formatNumber(dealItem.newDealActivity.size),
              sizeType: dealItem.newDealActivity.sizeType ? dealItem.newDealActivity.sizeType.name : null,
              dealContactIds: dealItem.newDealActivity.dealContacts && dealItem.newDealActivity.dealContacts.length
                ? dealItem.newDealActivity.dealContacts.map((contactItem: Contact): Contact => ({
                  id: contactItem.id,
                  firstName: contactItem.firstName,
                  lastName: contactItem.lastName,
                  fullName: contactItem.fullName,
                  photo: contactItem.photo,
                  photoThumbnail: contactItem.photoThumbnail,
                })) : [],
              tags: dealItem.newDealActivity.tags,
            } : null,
            dealStructure: dealItem.dealStructure,
            rfp: dealItem.rfp,
            askingPrice: FormatService.formatNumber(dealItem.askingPrice),
            equityRequired: FormatService.formatNumber(dealItem.equityRequired),
            estimatedTdc: FormatService.formatNumber(dealItem.estimatedTdc),
            loanType: dealItem.loanType,
            extensionConditions: dealItem.extensionConditions,
            equityAmount: FormatService.formatNumber(dealItem.equityAmount),
            totalFunded: FormatService.formatNumber(dealItem.totalFunded),
            totalUnfunded: FormatService.formatNumber(dealItem.totalUnfunded),
            dealFile: {},
          };
        });
      }
    });
  }

  return {
    isDataLoaded: !!dealList.dealListData,
    isExportMode: controls.isExportMode,
    isSelectMode: controls.isSelectMode,
    isEditMode: controls.isEditMode,
    isCustomizeMode: controls.isCustomizeMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isLoading: dealList.isLoading,
    isActivityCommentSaving: activity.isCommentSaving,
    isFormSaving: dealList.isFormSaving,
    currentUser: app.baseData && app.baseData.currentUser,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
    statusFilterStatus: statusFilter.statusFilter,
    newCommentList: activityList.commentList,
    isDealStatusChangeListLoading: deal.isDealStatusChangeListLoading,
    dealStatusChangeListData: dealList.dealStatusChangeListData,
    portfolioGroupList: dealList.dealListData && dealList.dealListData.portfolioGroupList,
    dealListData: dealList.dealListData && dealList.dealListData.dealList,
    dealsTotal: dealList.dealListData && dealList.dealListData.dealsTotal,
    dealTemplateList: dealList.dealListData && dealList.dealListData.dealTemplateList,
    dealStatusList: dealList.dealListData && dealList.dealListData.dealStatusList,
    dealRejectReasonList: dealList.dealListData && dealList.dealListData.dealRejectReasonList,
    assetTypeList: dealList.dealListData && dealList.dealListData.assetTypeList,
    contactPersonInternalList: dealList.dealListData && dealList.dealListData.contactPersonInternalList,
    contactCompanyList: dealList.dealListData && dealList.dealListData.contactCompanyList,
    contactPersonList: dealList.dealListData && dealList.dealListData.contactPersonList,
    dealTypeList: dealList.dealListData && dealList.dealListData.dealTypeList,
    measureUnitList: dealList.dealListData && dealList.dealListData.measureUnitList,
    assetOwnershipStructureTypeList: dealList.dealListData && dealList.dealListData.assetOwnershipStructureTypeList,
    dealLoanTypeList: dealList.dealListData && dealList.dealListData.dealLoanTypeList,
    accessLevelList: dealList.dealListData && dealList.dealListData.accessLevelList,
    contactLinkedUserList: dealList.dealListData && dealList.dealListData.contactLinkedUserList,
    tagList: dealList.dealListData && dealList.dealListData.tagList,
    userList: dealList.dealListData && dealList.dealListData.userList,
  };
};

const DealListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ dealList }: Reducers): ValidationErrorsMap => (
    dealList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ dealList }: Reducers): ValidationErrorsMap => (
    dealList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_DEAL_LIST}
      component={DealList}
      validator={DealListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(DealListForm);
